import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BottomNav from '../components/BottomNav';
import tora from '../../data/torah.json';
import { Pasuk } from '../../data/psukim';
import ashkenazAlyiot from '../../data/ashkenaz_alyiot.json';
import haftarotData from '../../data/haftarot.json';
import { saveVerseScore, loadScores, loadBestScores } from '../utils/progressStorage';
import { getRabbiAlignmentJsonUrl, getRabbiAudioUrl } from '../components/utils';
import {
  getParasha,
  getTradition,
  getLengthLimit,
  getVersesPerGroup,
  getLearningCurve,
  setLearningCurve,
  StoredLearningCurve,
  getSelectedSection,
} from '../utils/userSelections';
import { useTraditionRabbi } from '../utils/rabbiesFirestore';
import { useNavigate, useLocation } from 'react-router-dom';
import Share from '../icons/share.svg';
import {
  trackPageView,
  trackClick,
  trackSelection,
  trackPracticeAction,
  trackLearningCurveChange,
  trackComparisonResult,
  trackAudioPlayback,
  trackNavigation,
} from '../utils/analytics';
import { FIREBASE_FUNCTIONS, callCloudFunction } from '../utils/firebaseFunctions';
import { logRabbiAudioPlay, logMissingVerseNotifyRequest } from '../utils/auditLog';
import { saveVerseScoreToFirestore } from '../utils/verseScoresFirestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firebaseApp } from '../utils/firebaseConfig';
import { useHasActiveSubscription } from 'app/utils/useSubscription';
import { useShowPaymentsFlag } from 'app/utils/useShowPaymentsFlag';
import { formatHebrewText, renderHebrewWithShvaNa } from '../utils/hebrewTextUtils';
import { formatNumberForLocale, numberToHebrewLetters } from '../utils/hebrewNumbers';
import AIConsentDialog, { hasAIConsent, setAIConsent } from '../components/AIConsentDialog';
import ScoreCelebrationCheckmark from '../components/ScoreCelebrationCheckmark';

/** Lowercase emails that can use the app without paying (no subscription required). */
const FREE_ACCESS_EMAIL_WHITELIST: readonly string[] = [
  'shaul.almog.test@gmail.com',
  'shaul.almog@gmail.com',
  'shimibis@gmail.com',
  'david.cohen@example.com',
  'yznaman@gmail.com',
  '456david4@gmail.com',
  'ic431881@gmail.com',
];

function getCurrentUserEmail(): string | null {
  try {
    const stored = localStorage.getItem('loggedInUser');
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { email?: string };
    const raw = parsed?.email;
    return raw && String(raw).trim() !== '' ? String(raw).trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

function isGuestUser(): boolean {
  try {
    const stored = localStorage.getItem('loggedInUser');
    if (!stored) return true;
    const parsed = JSON.parse(stored) as { userId?: string; email?: string };
    // A real email means the user is authenticated, regardless of userId format
    if (parsed?.email && String(parsed.email).trim() !== '') return false;
    // No email → guest
    return true;
  } catch {
    return true;
  }
}

const COMPARE_COUNT_KEY = 'compareCount';
/** Unpaid users get this many free comparisons; once reached, Record and Compare are locked. */
const FREE_COMPARE_LIMIT = 3;

function getCompareCount(): number {
  try {
    return parseInt(localStorage.getItem(COMPARE_COUNT_KEY) ?? '0', 10) || 0;
  } catch {
    return 0;
  }
}

function incrementCompareCount(): number {
  const next = getCompareCount() + 1;
  try {
    localStorage.setItem(COMPARE_COUNT_KEY, String(next));
  } catch {
    /* ignore */
  }
  return next;
}

// Color constants (matching AppOnboardingPage theme)
const BLUE_ACCENT = '#3B7BF4';
const GOLD_BUTTON = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

/** Set to true to show the pay/subscription message and lock Record/Compare after verse 3 for users without access. */
const PAYMENT_RESTRICTION_ENABLED = true;

interface TorahVerse extends Pasuk {
  parasha?: string;
}

type AliyahRange = {
  start: { chapter: number; pasuk: number };
  end: { chapter: number; pasuk: number };
};

type LearningMode = 'pasuk1' | 'pasuk3' | 'aliyah' | 'haftara';

type HaftaraItem = {
  pasuk: number;
  book: string;
  chapter: number;
  he: string;
  en?: string;
};

type HaftaraEntry = {
  nusach: string;
  parsha: string;
  haftara: HaftaraItem[];
};

const getRecordingMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
};

const audioMimeToExtension = (mimeType: string): string => {
  if (mimeType.includes('webm')) return 'webm';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'mp4';
  return 'wav';
};

/**
 * The haftara text in haftarot.json carries light HTML markup (<small>, <span>,
 * <br>, <b>) and the &thinsp;/&nbsp; entities. Strip the tags (keeping their
 * inner Hebrew, e.g. the paseq ׀) and normalize spacing so it renders like the
 * plain Torah verse text.
 */
const cleanHaftaraHebrew = (raw: string): string =>
  raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&thinsp;|&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Find the haftara psukim for a parasha (by Hebrew name) and nusach, mapped into
 * the same TorahVerse shape used for the Torah reading. Falls back to the
 * Ashkenaz haftara when the exact nusach has no entry.
 */
const getHaftaraVerses = (
  _parashaHebrew: string | undefined,
  parashaEnglish: string | undefined,
  nusach: string,
): TorahVerse[] => {
  if (!parashaEnglish) return [];
  const target = parashaEnglish.toLowerCase().replace(/'/g, '');
  const entries = (haftarotData as HaftaraEntry[]).filter(
    (h) => h.parsha.toLowerCase().replace(/'/g, '') === target,
  );
  if (entries.length === 0) return [];
  const entry =
    entries.find((h) => h.nusach === nusach) ??
    entries.find((h) => h.nusach === 'ashkenaz') ??
    entries[0];
  return entry.haftara.map((item, idx) => ({
    id: idx,
    book: item.book,
    chapter: item.chapter,
    pasuk: item.pasuk,
    hebrew: cleanHaftaraHebrew(item.he),
    parasha: parashaEnglish,
  }));
};

const parseAliyahRange = (range: string): AliyahRange | null => {
  const [startStr, endStr] = range.split('-');
  if (!startStr || !endStr) return null;
  const [startChapter, startPasuk] = startStr.split(':').map(Number);
  const [endChapter, endPasuk] = endStr.split(':').map(Number);
  if (
    Number.isNaN(startChapter) ||
    Number.isNaN(startPasuk) ||
    Number.isNaN(endChapter) ||
    Number.isNaN(endPasuk)
  )
    return null;
  return {
    start: { chapter: startChapter, pasuk: startPasuk },
    end: { chapter: endChapter, pasuk: endPasuk },
  };
};

const isVerseWithinRange = (verse: TorahVerse, range: AliyahRange): boolean => {
  const cmp = (a: { chapter: number; pasuk: number }, b: { chapter: number; pasuk: number }) => {
    if (a.chapter !== b.chapter) return a.chapter - b.chapter;
    return a.pasuk - b.pasuk;
  };
  return cmp(verse, range.start) >= 0 && cmp(verse, range.end) <= 0;
};

const CompareLimitUpgradeButton: React.FC<{
  isHebrew: boolean;
  userEmail: string | null;
  onDone: () => void;
}> = ({ isHebrew, userEmail, onDone }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    trackClick('Upgrade Now - Compare Limit', 'PracticePage', { userEmail });
    onDone();
    navigate('/payments');
  };

  return (
    <button
      data-aid="compare-limit-upgrade-button"
      onClick={handleClick}
      style={{
        width: '100%',
        padding: '12px 0',
        borderRadius: 9999,
        background: GOLD_BUTTON,
        border: 'none',
        color: TEXT_DARK,
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {isHebrew ? 'שדרג עכשיו' : 'Upgrade now'}
    </button>
  );
};

const PracticePage: React.FC = () => {
  const { t, i18n } = useTranslation();

  // nusach and tradition are the same concept; the persisted tradition key is the nusach
  const nusach = getTradition()?.key || 'ashkenaz';
  const lengthLimit = getLengthLimit();

  const navigate = useNavigate();
  const location = useLocation();
  const _navState = (location.state ?? {}) as {
    aliyahIndex?: number;
    mode?: LearningMode;
    versesPerGroup?: number | 'half' | 'all';
    startVerseIndex?: number;
  };
  const selectedParasha = getParasha();
  const parashaKey = selectedParasha?.english;
  const { hasActiveSubscription } = useHasActiveSubscription();
  const showPayments = useShowPaymentsFlag();
  const [userEmail, setUserEmail] = useState<string | null>(() => getCurrentUserEmail());

  useEffect(() => {
    const stored = localStorage.getItem('loggedInUser');
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as { email?: string };
        const e = parsed?.email;
        setUserEmail(e && String(e).trim() ? String(e).trim().toLowerCase() : null);
        return;
      } catch {
        /* fall through to auth */
      }
    }
    const auth = getAuth(firebaseApp);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserEmail(user?.email?.trim() ? user.email.trim().toLowerCase() : null);
    });
    return () => unsubscribe();
  }, []);

  const isWhitelisted =
    userEmail != null &&
    FREE_ACCESS_EMAIL_WHITELIST.length > 0 &&
    FREE_ACCESS_EMAIL_WHITELIST.includes(userEmail);
  const hasAccess = hasActiveSubscription || isWhitelisted;
  const [selectedAliyah, setSelectedAliyah] = useState(() => {
    if (_navState.aliyahIndex !== undefined) return _navState.aliyahIndex;
    const stored = getSelectedSection();
    if (stored?.type === 'aliyah' && stored.aliyahIndex !== undefined) return stored.aliyahIndex;
    return 0;
  });
  const [learningMode, setLearningMode] = useState<LearningMode>(() => {
    if (_navState.mode) return _navState.mode;
    const stored = getLearningCurve();
    const versesCount = stored?.versesCount ?? getVersesPerGroup();
    if (versesCount === 3) return 'pasuk3';
    if (versesCount === 'all') return 'aliyah';
    return 'pasuk1';
  });

  // NEW: cache alignments per verse index (lazy loaded when selected)
  const [alignmentsByVerse, setAlignmentsByVerse] = useState<Record<number, any[]>>({});
  const [verseIndex, setVerseIndex] = useState(() => _navState.startVerseIndex ?? 0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingIndex, setRecordingIndex] = useState<number | null>(null);
  const [recordBlobs, setRecordBlobs] = useState<Record<number, Blob>>({});
  // NEW: track user audio URLs from S3 (keyed by verse index)
  const [userAudioUrls, setUserAudioUrls] = useState<Record<number, string>>({});
  // Track which verses have been checked for S3 recordings
  const checkedVersesRef = useRef<Set<number>>(new Set());
  const [isComparing, setIsComparing] = useState(false);
  const [comparingIndex, setComparingIndex] = useState<number | null>(null);
  const [overallScore, setOverallScore] = useState<number | null>(null);
  // Store comparison results per group (keyed by startIndex)
  const [comparisonResultsByGroup, setComparisonResultsByGroup] = useState<
    Record<number, { results: any; score: number }>
  >({});
  // Track newly rendered results for blink animation
  const [newResultsSet, setNewResultsSet] = useState<Set<number>>(new Set());
  // High-score checkmark celebration: which verse's trophy badge to converge into
  const [celebration, setCelebration] = useState<{ idx: number; nonce: number } | null>(null);
  const scoreBadgeRefs = useRef<Map<number, React.RefObject<HTMLButtonElement>>>(new Map());
  const getScoreBadgeRef = (idx: number): React.RefObject<HTMLButtonElement> => {
    let ref = scoreBadgeRefs.current.get(idx);
    if (!ref) {
      ref = { current: null };
      scoreBadgeRefs.current.set(idx, ref);
    }
    return ref;
  };
  const pasukContainerRefs = useRef<Map<number, React.RefObject<HTMLDivElement>>>(new Map());
  const getPasukContainerRef = (idx: number): React.RefObject<HTMLDivElement> => {
    let ref = pasukContainerRefs.current.get(idx);
    if (!ref) {
      ref = { current: null };
      pasukContainerRefs.current.set(idx, ref);
    }
    return ref;
  };
  const [scoresByVerse, setScoresByVerse] = useState<Record<number, number>>({});
  const [lastScoresByVerse, setLastScoresByVerse] = useState<Record<number, number>>({}); // new: last attempt scores
  // New state for playing user recordings
  const [isUserPlaying, setIsUserPlaying] = useState(false);
  const [userPlayingIndex, setUserPlayingIndex] = useState<number | null>(null);
  // Grouped mode: enabled if user selected a learning curve, or via ProfilePage setting
  const [grouped, setGrouped] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    // Explicit versesPerGroup from nav state wins
    if (_navState.versesPerGroup !== undefined) return _navState.versesPerGroup !== 1;
    // Haftara is practiced one pasuk at a time, not as a single whole-haftara group
    if (_navState.mode === 'haftara') return false;
    // If user has explicitly set a learning curve, enable grouping automatically
    const learningCurveSetting = getLearningCurve();
    if (learningCurveSetting !== null) return true;
    // Otherwise fall back to profile setting
    return localStorage.getItem('groupedVerses') === 'true';
  });
  // NEW: state for group playback & recording
  const [groupPlaybackQueue, setGroupPlaybackQueue] = useState<number[]>([]);
  // NEW: state for current timed word highlighting of target verse
  const [currentWordIndex, setCurrentWordIndex] = useState<number | null>(null);
  // NEW: state for current verse index in group during recording (for karaoke mode)
  const [currentVerseInGroup, setCurrentVerseInGroup] = useState<number | null>(null);
  // NEW: state for current verse index in group during playback (for karaoke mode)
  const [, setCurrentVerseInPlaybackGroup] = useState<number | null>(null);
  // State for showing rabbi audio not found popup
  const [showRabbiAudioNotFound, setShowRabbiAudioNotFound] = useState(false);
  const [missingVerseInfo, setMissingVerseInfo] = useState<{
    book: string | number;
    chapter: number;
    pasuk: number;
    verseIndex: number;
  } | null>(null);
  const [missingVerseNotifySent, setMissingVerseNotifySent] = useState(false);
  // State for review request sending
  // One-time AI consent dialog before first Compare
  const [showAIConsentDialog, setShowAIConsentDialog] = useState(false);
  const [pendingCompareIndex, setPendingCompareIndex] = useState<number | null>(null);
  const [showGuestLoginPopup, setShowGuestLoginPopup] = useState(false);
  const [showCompareLimitPopup, setShowCompareLimitPopup] = useState(false);
  // Force re-render when parasha is changed via dropdown (getParasha() reads from localStorage)
  // Playback time and duration state for progress display
  const [playbackTime, setPlaybackTime] = useState<number>(0);
  const [, setPlaybackDuration] = useState<number>(0);
  // Index of target verse (book 4, chapter 25, pasuk 11) for highlighting
  // Haftara psukim for the current parasha + nusach (same shape as Torah verses)
  const haftaraVerses: TorahVerse[] = useMemo(
    () => getHaftaraVerses(selectedParasha?.hebrew, parashaKey, nusach),
    [selectedParasha?.hebrew, parashaKey, nusach],
  );

  const parashaVerses: TorahVerse[] = useMemo(() => {
    // In haftara mode the reading comes from haftarot.json instead of torah.json
    if (learningMode === 'haftara') return haftaraVerses;
    if (!parashaKey) return [];
    return (tora as TorahVerse[])
      .filter(
        (v) =>
          v.parasha &&
          v.parasha.toLowerCase().replace("'", '') === parashaKey.toLowerCase().replace("'", ''),
      )
      .slice(0, lengthLimit);
  }, [parashaKey, lengthLimit, learningMode, haftaraVerses]);

  const aliyahRanges: AliyahRange[] = useMemo(() => {
    if (!parashaKey) return [];
    const ranges = (ashkenazAlyiot as Record<string, string[]>)[parashaKey] || [];
    return ranges.map((r) => parseAliyahRange(r)).filter(Boolean) as AliyahRange[];
  }, [parashaKey]);

  const versesWithGlobalIndex = useMemo(() => {
    if (!parashaKey) return [];
    const pairs = parashaVerses.map((v, idx) => ({
      verse: v,
      globalIndex: idx,
    }));
    if (!aliyahRanges.length || learningMode === 'haftara') return pairs;
    const range = aliyahRanges[selectedAliyah] || aliyahRanges[aliyahRanges.length - 1];
    return pairs.filter(({ verse }) => isVerseWithinRange(verse, range));
  }, [aliyahRanges, learningMode, parashaKey, parashaVerses, selectedAliyah]);

  const verses: TorahVerse[] = useMemo(
    () => versesWithGlobalIndex.map((p) => p.verse),
    [versesWithGlobalIndex],
  );

  const verseGlobalIndices: number[] = useMemo(
    () => versesWithGlobalIndex.map((p) => p.globalIndex),
    [versesWithGlobalIndex],
  );

  // Audio + recording refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastRabbiUrlRef = useRef<string>('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const userAudioRef = useRef<HTMLAudioElement>(null);
  const userObjectUrlsRef = useRef<Record<number, string>>({});
  // Ref mirror of recordBlobs for synchronous access before React state updates propagate
  const recordBlobsRef = useRef<Record<number, Blob>>({});
  // Track individual verse elements for reliable scroll restoration
  const verseRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // NEW: ref to the actual main scrollable area (outer container) for better scrolling
  const mainScrollRef = useRef<HTMLDivElement | null>(null);
  // NEW: ref to track recording start time for karaoke mode
  const recordingStartTimeRef = useRef<number | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // NEW: refs for lazy-loading user recordings via IntersectionObserver
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingCheckRef = useRef<Set<number>>(new Set());
  const checkTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Verses for selected parasha (limit length)

  // Track page view on mount
  useEffect(() => {
    trackPageView('Practice', { parasha: parashaKey });
  }, [parashaKey]);

  // Reset aliyah selection and cached alignments when parasha changes
  useEffect(() => {
    setAlignmentsByVerse({});
    setRecordBlobs({});
    userObjectUrlsRef.current = {};
    recordBlobsRef.current = {};
    if (_navState.aliyahIndex !== undefined) return;
    setSelectedAliyah(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parashaKey]);

  // Clear cached alignments when switching aliyah/mode to avoid mismatched indices
  useEffect(() => {
    setVerseIndex(0);
    setAlignmentsByVerse({});
    setRecordBlobs({});
    userObjectUrlsRef.current = {};
    recordBlobsRef.current = {};
  }, [selectedAliyah, learningMode]);

  // Reset verseIndex if list shrinks
  useEffect(() => {
    if (verseIndex >= verses.length) setVerseIndex(0);
  }, [verses.length, verseIndex]);

  const progressPercent = verses.length ? Math.round(((verseIndex + 1) / verses.length) * 100) : 0;
  // Rabbi resolved from the rabbiTraditionDefaults map for the persisted tradition
  // (an explicit ChooseRabbiPage selection that matches the tradition wins)
  const parashaRabbi = useTraditionRabbi('parasha');
  const haftaraRabbi = useTraditionRabbi('haftara');
  const getCurrentRabbi = () => (learningMode === 'haftara' ? haftaraRabbi : parashaRabbi);
  const getRabiInitials = (): string => {
    const rabbi = getCurrentRabbi();
    return rabbi?.initials || rabbi?.key || rabbi?.id || '';
  };
  const hasAliyahOptions = aliyahRanges.length > 0;
  const learningCurveOptions = useMemo(() => {
    const baseOptions: Array<{
      key: string;
      label: string;
      aliyahIndex?: number;
    }> = [
      {
        key: 'pasuk1',
        label: i18n.language === 'he' ? 'פסוק 1' : '1 Pasuk',
      },
      {
        key: 'pasuk3',
        label: i18n.language === 'he' ? '3 פסוקים' : '3 Psukim',
      },
    ];

    if (hasAliyahOptions) {
      baseOptions.push({
        key: 'aliyah-full',
        label: i18n.language === 'he' ? 'עלייה מלאה' : 'Full Aliyah',
      });
    }

    if (haftaraVerses.length > 0) {
      baseOptions.push({
        key: 'haftara',
        label: i18n.language === 'he' ? 'הפטרה' : 'Haftara',
      });
    }

    return baseOptions;
  }, [hasAliyahOptions, i18n.language, haftaraVerses.length]);

  const fetchAlignments = async (pasuk: Pasuk) => {
    try {
      const alignmentUrl = getRabbiAlignmentJsonUrl(pasuk, nusach, getRabiInitials());
      const alignment = await fetch(alignmentUrl).then((res) => res.json());
      // Sanitize alignment before storing
      let sanitized = sanitizeAlignment(Array.isArray(alignment) ? alignment : []);

      // Update sanitized alignment's 'word' property to be the array of pasuk's Hebrew words (from pasuk.hebrew)
      // First, split pasuk.hebrew into words
      let pasukWords: string[] = [];
      if (typeof pasuk.hebrew === 'string') {
        pasukWords = pasuk.hebrew.replace(/־/g, ' ').split(/\s+/).filter(Boolean);
      }

      // Assign the real Hebrew word from pasuk.words (or pasuk.hebrew split) to each alignment part
      sanitized = sanitized.map((item, i) => ({
        ...item,
        word: pasukWords[i] || item.word, // use pasukWords if available, else fallback
      }));

      setAlignmentsByVerse((prev) => ({
        ...prev,
        [verses.indexOf(pasuk as any)]: sanitized,
      }));
      // If we are currently playing or recording this verse/group, reset marker to first word
      const idx = verses.indexOf(pasuk as any);
      if (idx === playingIndex || (isRecording && idx === recordingIndex)) {
        setCurrentWordIndex(0);
      }
    } catch (error) {
      console.error(
        'Failed to fetch or process alignment, probably no alignment file for this verse',
        error,
      );
    }
  };

  // Sanitize alignment: ensure numeric ordered start/end, fill missing starts, avoid zero-length
  const sanitizeAlignment = (
    raw: any[],
  ): { start: number; end: number; text?: string; word?: string }[] => {
    const mapped = raw
      .map((w) => {
        const rs = typeof w.start === 'string' ? parseFloat(w.start) : w.start;
        const re = typeof w.end === 'string' ? parseFloat(w.end) : w.end;
        return {
          start: typeof rs === 'number' && !isNaN(rs) ? rs : NaN,
          end: typeof re === 'number' && !isNaN(re) ? re : NaN,
          text: w.text,
          word: w.word,
        };
      })
      .filter((w) => !(isNaN(w.start) && isNaN(w.end))); // drop entries with no usable timing

    if (mapped.length === 0) return [];

    const hasValidStarts = mapped.some((w) => !isNaN(w.start));
    // If no valid starts but have ends: fabricate sequential starts
    if (!hasValidStarts) {
      // Sort by end ascending
      mapped.sort((a, b) => (isNaN(a.end) ? 1 : isNaN(b.end) ? -1 : a.end - b.end));
      let prevEnd = 0;
      for (const w of mapped) {
        if (isNaN(w.end)) {
          w.start = prevEnd;
          w.end = prevEnd + 0.4; // fallback duration
        } else {
          w.start = prevEnd;
          // Ensure positive duration
          if (w.end <= w.start) w.end = w.start + 0.25;
          prevEnd = w.end;
        }
      }
    }

    // Replace NaN starts with previous valid end
    for (let i = 0; i < mapped.length; i++) {
      if (isNaN(mapped[i].start)) {
        let prevValid = 0;
        for (let j = i - 1; j >= 0; j--) {
          if (!isNaN(mapped[j].end)) {
            prevValid = mapped[j].end;
            break;
          }
        }
        mapped[i].start = prevValid;
      }
      if (isNaN(mapped[i].end)) {
        // Give a small duration if missing
        mapped[i].end = mapped[i].start + 0.3;
      }
      // Clamp negatives
      if (mapped[i].start < 0) mapped[i].start = 0;
      if (mapped[i].end < mapped[i].start) mapped[i].end = mapped[i].start + 0.15;
    }

    // Sort by start ascending
    mapped.sort((a, b) => a.start - b.start);
    // Enforce monotonic end >= previous end
    let lastEnd = 0;
    for (const w of mapped) {
      if (w.end < w.start + 0.05) w.end = w.start + 0.05; // minimal duration
      if (w.start < lastEnd && w.end < lastEnd) {
        // Shift forward to avoid overlapping backwards
        const shift = lastEnd - w.start + 0.01;
        w.start += shift;
        w.end += shift;
      }
      if (w.end < lastEnd) w.end = lastEnd + 0.02;
      lastEnd = w.end;
    }
    return mapped;
  };

  const handlePlay = async (idx: number, verse: TorahVerse) => {
    if (!audioRef.current || !verse) return;
    const rabbiUrl = getRabbiAudioUrl(verse, nusach, getRabiInitials());
    // Toggle pause (only for single verse, not during group playback)
    if (isPlaying && playingIndex === idx && groupPlaybackQueue.length === 0) {
      audioRef.current.pause();
      setIsPlaying(false);
      trackPracticeAction('pause', idx, { parasha: parashaKey });
      trackAudioPlayback('pause', 'rabbi', idx);
      return;
    }
    trackPracticeAction('play', idx, { parasha: parashaKey });
    trackAudioPlayback('play', 'rabbi', idx);
    // Ensure source loaded (do not reset any external restart state unless changing verse)
    try {
      const audioEl = audioRef.current;
      const needsReload = lastRabbiUrlRef.current !== rabbiUrl || audioEl.src === '';
      if (needsReload) {
        audioEl.src = rabbiUrl;
        lastRabbiUrlRef.current = rabbiUrl;
        // Wait for audio to be ready before playing
        await new Promise<void>((resolve, reject) => {
          const onCanPlay = () => {
            audioEl.removeEventListener('canplaythrough', onCanPlay);
            audioEl.removeEventListener('error', onError);
            resolve();
          };
          const onError = () => {
            audioEl.removeEventListener('canplaythrough', onCanPlay);
            audioEl.removeEventListener('error', onError);
            reject(new Error('Audio load failed'));
          };
          audioEl.addEventListener('canplaythrough', onCanPlay);
          audioEl.addEventListener('error', onError);
          audioEl.load();
        });
      }
      // If previously restarted (paused at 0) keep currentTime as-is; otherwise leave currentTime untouched
      await audioEl.play();
      setPlayingIndex(idx);
      setIsPlaying(true);

      // Persist last played verse (global index so ParashaOverviewPage can match it)
      if (parashaKey) {
        try {
          const globalIdx = versesWithGlobalIndex[idx]?.globalIndex ?? idx;
          localStorage.setItem(`lastComparedVerse:${parashaKey}`, String(globalIdx));
        } catch {
          /* ignore */
        }
      }

      // Log audit entry for rabbi audio play
      const rabbi = getCurrentRabbi();
      if (rabbi?.id) {
        const userId = getUserId();
        if (userId) {
          let userName = '';
          try {
            const storedUser = localStorage.getItem('loggedInUser');
            if (storedUser) {
              const parsed = JSON.parse(storedUser);
              userName = parsed.displayName || '';
            }
          } catch {
            /* ignore */
          }
          logRabbiAudioPlay({
            userId,
            userName,
            userEmail: userId,
            rabbiId: rabbi.id,
            rabbiInitials: rabbi.initials || '',
            parashaKey: parashaKey || '',
            parashaHebrew: selectedParasha?.hebrew || '',
            book: verse.book,
            chapter: verse.chapter,
            pasuk: verse.pasuk,
            verseIndex: idx,
            nusach,
          }).catch((err) => console.error('Failed to log rabbi audio play:', err));
        }
      }
    } catch (e) {
      console.error('playback failed', e);
      // Show popup if audio file not found
      setMissingVerseInfo({
        book: verse.book,
        chapter: verse.chapter,
        pasuk: verse.pasuk,
        verseIndex: idx,
      });
      setMissingVerseNotifySent(false);
      setShowRabbiAudioNotFound(true);
    }
  };

  const handleNotifyWhenVerseReady = async (): Promise<void> => {
    setMissingVerseNotifySent(true);
    const userId = getUserId();
    if (!userId || !missingVerseInfo) return;
    try {
      await logMissingVerseNotifyRequest({
        userId,
        parashaKey: parashaKey || '',
        book: missingVerseInfo.book,
        chapter: missingVerseInfo.chapter,
        pasuk: missingVerseInfo.pasuk,
        verseIndex: missingVerseInfo.verseIndex,
        nusach,
      });
    } catch (err) {
      console.error('Failed to log missing verse notify request:', err);
    }
  };

  // NEW: play entire group sequentially
  const handlePlayGroup = async (groupIndices: number[]) => {
    if (!audioRef.current || groupIndices.length === 0) return;
    // If already playing any verse in this group -> pause (do NOT reset marker here)
    const isCurrentlyPlayingThisGroup =
      isPlaying &&
      (groupIndices.includes(playingIndex!) ||
        groupPlaybackQueue.some((idx) => groupIndices.includes(idx)));
    if (isCurrentlyPlayingThisGroup) {
      audioRef.current.pause();
      setIsPlaying(false);
      setGroupPlaybackQueue([]);
      return;
    }
    // RESET karaoke marker when starting a NEW group playback
    setCurrentWordIndex(null);
    setCurrentVerseInPlaybackGroup(null);
    setCurrentVerseInGroup(null);
    // Ensure alignments are loaded for all verses in the group for karaoke mode
    for (const verseIdx of groupIndices) {
      const verse = verses[verseIdx];
      if (verse && !alignmentsByVerse[verseIdx]) {
        await fetchAlignments(verse).catch((e) =>
          console.error('Failed fetching alignment for playback', verseIdx, e),
        );
      }
    }
    // Prepare queue (excluding first which we start immediately)
    const queue = [...groupIndices];
    const firstIdx = queue.shift()!;
    setGroupPlaybackQueue(queue);
    await handlePlay(firstIdx, verses[firstIdx]);
  };

  const startRecording = async (idx: number) => {
    // Unpaid users who exhausted their free comparisons cannot record either
    if (!hasAccess && getCompareCount() >= FREE_COMPARE_LIMIT) {
      setShowCompareLimitPopup(true);
      return;
    }
    trackPracticeAction('record_start', idx, { parasha: parashaKey });
    trackClick('Record Start', 'Practice', { verse_index: idx });
    // RESET karaoke marker when starting a NEW recording
    setCurrentWordIndex(null);
    setCurrentVerseInPlaybackGroup(null);
    setCurrentVerseInGroup(null);
    // If grouped mode: determine group indices and record all together
    let targetIndices: number[] = [idx];
    if (grouped) {
      const group = groupedVerses.find(
        (g) => idx >= g.startIndex && idx < g.startIndex + g.verses.length,
      );
      if (group) targetIndices = group.verses.map((_, i) => group.startIndex + i);
    }
    // Clear previous comparison result so re-compare uses the new recording
    setComparisonResultsByGroup((prev) => {
      const next = { ...prev };
      targetIndices.forEach((i) => delete next[i]);
      return next;
    });
    // Stop user playback if recording starts
    if (isUserPlaying) {
      if (userAudioRef.current) userAudioRef.current.pause();
      setIsUserPlaying(false);
      setUserPlayingIndex(null);
    }
    // Ensure alignment data is loaded for karaoke mode for ALL verses in group
    for (const verseIdx of targetIndices) {
      const verse = verses[verseIdx];
      if (verse && !alignmentsByVerse[verseIdx]) {
        await fetchAlignments(verse).catch((e) =>
          console.error('Failed fetching alignment for recording', verseIdx, e),
        );
      }
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recMimeType = getRecordingMimeType();
      const mr = new MediaRecorder(stream, recMimeType ? { mimeType: recMimeType } : undefined);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (ev) => chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        const actualMimeType = mediaRecorderRef.current?.mimeType || recMimeType || 'audio/wav';
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        // Assign same blob to all verses recorded in this session
        for (const verseIdx of targetIndices) {
          const prevUrl = userObjectUrlsRef.current[verseIdx];
          if (prevUrl) URL.revokeObjectURL(prevUrl);
          userObjectUrlsRef.current[verseIdx] = URL.createObjectURL(blob);
          // Store in ref synchronously so handleCompare can access it before state updates
          recordBlobsRef.current[verseIdx] = blob;
        }
        setRecordBlobs((prev) => {
          const updated = { ...prev };
          targetIndices.forEach((i2) => {
            updated[i2] = blob;
          });
          return updated;
        });
        stream.getTracks().forEach((t) => t.stop());

        // Upload the recording to S3 for each verse in the group
        for (const verseIdx of targetIndices) {
          const uploadedUrl = await uploadUserRecording(verseIdx, blob);
          if (uploadedUrl) {
            setUserAudioUrls((prev) => ({ ...prev, [verseIdx]: uploadedUrl }));
          }
        }
      };
      mr.start();
      setRecordingIndex(idx); // keep anchor verse index for UI
      setIsRecording(true);
      // Start tracking time for karaoke mode
      recordingStartTimeRef.current = Date.now();
      // Initialize current verse in group for karaoke mode
      setCurrentVerseInGroup(0);
    } catch (e) {
      console.error('record failed', e);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      trackPracticeAction('record_stop', recordingIndex ?? undefined, {
        parasha: parashaKey,
      });
      trackClick('Record Stop', 'Practice', { verse_index: recordingIndex });
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingIndex(null);
      // Stop karaoke mode timer
      recordingStartTimeRef.current = null;
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      // Clear current word highlight
      setCurrentWordIndex(null);
      setCurrentVerseInGroup(null);
    }
  };

  const handleCompare = async (idx: number) => {
    // Guest users cannot compare — prompt them to log in
    if (isGuestUser()) {
      setShowGuestLoginPopup(true);
      return;
    }
    // Unpaid users are limited to FREE_COMPARE_LIMIT comparisons
    if (!hasAccess && getCompareCount() >= FREE_COMPARE_LIMIT) {
      setShowCompareLimitPopup(true);
      return;
    }
    // Increment count for unpaid users before the API call
    if (!hasAccess) incrementCompareCount();

    trackPracticeAction('compare', idx, { parasha: parashaKey });
    trackClick('Compare', 'Practice', { verse_index: idx });
    // Compare either single verse or entire group when grouped
    let targetIndices: number[] = [idx];
    if (grouped) {
      const group = groupedVerses.find(
        (g) => idx >= g.startIndex && idx < g.startIndex + g.verses.length,
      );
      if (group) targetIndices = group.verses.map((_, i) => group.startIndex + i);
    }

    // Determine if user recording is only in S3 (not locally recorded this session)
    // Fall back to ref in case React state hasn't updated yet (race condition after stop)
    const localRecordBlob = recordBlobs[idx] ?? recordBlobsRef.current[idx];
    const userS3Url = userAudioUrls[idx];
    const hasLocalRecording = !!localRecordBlob;
    const hasS3Recording = !!userS3Url;

    // Need either local recording or S3 URL
    if (!hasLocalRecording && !hasS3Recording) return;
    if (isRecording && recordingIndex === idx) return;

    setIsComparing(true);
    setComparingIndex(idx);
    try {
      // Get rabbi audio URL - server will fetch it directly from S3/CloudFront
      const firstVerse = verses[targetIndices[0]];
      const rabbiAudioUrl = getRabbiAudioUrl(firstVerse, nusach, getRabiInitials());

      // Get student audio as base64
      let studentAudioBase64: string;
      if (hasLocalRecording) {
        studentAudioBase64 = await blobToBase64(localRecordBlob!);
      } else {
        // Fetch from S3 URL and convert to base64
        const studentResp = await fetch(userS3Url!);
        if (!studentResp.ok) {
          throw new Error('Failed to fetch student audio from S3');
        }
        const studentBlob = await studentResp.blob();
        studentAudioBase64 = await blobToBase64(studentBlob);
      }

      // Call the AI-powered word-by-word comparison endpoint
      // Server will fetch rabbi audio from the provided URL
      const response = await callCloudFunction('comparePasuk', FIREBASE_FUNCTIONS.comparePasuk(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rabbiAudioUrl,
          studentAudio: studentAudioBase64,
          mimeType: 'audio/mp3',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Compare API error:', errorText);
        setComparisonResultsByGroup((prev) => ({
          ...prev,
          [idx]: { results: null, score: -1 },
        }));
        return;
      }

      const data = await response.json();

      // New API: comparison is { explanation, total_score } (total_score 1-10 → percentage)
      const comparison = data.comparison || {};
      const totalScore = typeof comparison.total_score === 'number' ? comparison.total_score : 0;
      const avgScore = Math.round(totalScore * 10);

      setOverallScore(avgScore);

      // Store comparison results for this group (explanation + total_score)
      setComparisonResultsByGroup((prev) => ({
        ...prev,
        [idx]: { results: data, score: avgScore },
      }));

      // High score: play the checkmark celebration converging into this verse's trophy badge
      if (avgScore >= 80) {
        setCelebration({ idx, nonce: Date.now() });
      }

      // Mark this result as new for blink animation
      setNewResultsSet((prev) => new Set(prev).add(idx));
      setTimeout(() => {
        setNewResultsSet((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      }, 3000);

      // Track comparison result
      trackComparisonResult(avgScore, idx, parashaKey);

      // Persist last compared verse (global index so ParashaOverviewPage can match it)
      if (parashaKey) {
        try {
          const globalIdx = versesWithGlobalIndex[idx]?.globalIndex ?? idx;
          localStorage.setItem(`lastComparedVerse:${parashaKey}`, String(globalIdx));
          if (mainScrollRef.current) {
            localStorage.setItem(
              `lastScrollTop:${parashaKey}`,
              String(mainScrollRef.current.scrollTop),
            );
          }
          localStorage.setItem(`restoreScroll:${parashaKey}`, 'true');
        } catch {
          /* ignore */
        }
      }

      try {
        const parashaObj = JSON.parse(localStorage.getItem('selectedParasha') || 'null');
        const parashaName = parashaObj?.english || 'Unknown';
        // Save score for each verse in the group (local)
        for (const vIdx of targetIndices) {
          const globalVerseIndex = verseGlobalIndices[vIdx] ?? vIdx;
          const verseKey = `${parashaName}:${globalVerseIndex}`;
          saveVerseScore({
            verseKey,
            parasha: parashaName,
            verseIndex: globalVerseIndex,
            score: avgScore,
            pronunciation: avgScore,
            fluency: avgScore,
            tone: avgScore,
            durationSec: 0,
          });
          // Persist best score to Firestore so rabbi can see it on student details
          try {
            const raw = localStorage.getItem('loggedInUser');
            if (raw) {
              const user = JSON.parse(raw) as {
                userId?: string;
                uid?: string;
                email?: string;
              };
              const userId = user?.userId ?? user?.uid ?? user?.email ?? null;
              if (userId) {
                saveVerseScoreToFirestore(
                  userId,
                  verseKey,
                  parashaName,
                  globalVerseIndex,
                  avgScore,
                ).catch(() => {});
              }
            }
          } catch {
            /* ignore */
          }
        }
      } catch {
        /* ignore */
      }
    } catch (e) {
      console.error('compare failed', e);
      alert('Comparison failed: ' + (e instanceof Error ? e.message : 'Unknown error'));
    } finally {
      setIsComparing(false);
      setComparingIndex(null);
    }
  };

  // Handle viewing detailed comparison results - opens graph visualization
  const handleViewResults = (idx: number) => {
    trackNavigation('Practice', 'CompareResults', 'view_results');

    // Get cached comparison data (already fetched when Compare was clicked)
    const cachedResult = comparisonResultsByGroup[idx];

    navigate('/compare-results', {
      state: {
        cachedData: cachedResult?.results || null,
        verseIndex: idx,
        parasha: parashaKey,
      },
    });
  };

  // Restore last compared verse when returning from accuracy page
  useEffect(() => {
    if (!parashaKey || !verses.length) return;
    if (_navState.aliyahIndex !== undefined) return;
    try {
      const saved = localStorage.getItem(`lastComparedVerse:${parashaKey}`);
      if (saved !== null) {
        const v = parseInt(saved, 10);
        if (!isNaN(v) && v >= 0 && v < verses.length) {
          setVerseIndex(v);
        }
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parashaKey, verses.length]);

  // Restore verse index and scroll position on mount
  useLayoutEffect(() => {
    if (!parashaKey) return;
    const versesReady = verses.length > 0;
    if (!versesReady) return;
    if (_navState.aliyahIndex !== undefined) return;
    // restore verseIndex if saved
    try {
      const savedVerse = localStorage.getItem(`lastComparedVerse:${parashaKey}`);
      if (savedVerse !== null) {
        const v = parseInt(savedVerse, 10);
        if (!isNaN(v) && v >= 0 && v < verses.length) {
          setVerseIndex(v);
        }
      }
    } catch {
      /* ignore */
    }
    // After setting verse index, attempt scroll restoration synchronously
    const restoreFlag = localStorage.getItem(`restoreScroll:${parashaKey}`) === 'true';
    if (restoreFlag && mainScrollRef.current) {
      const savedTop = localStorage.getItem(`lastScrollTop:${parashaKey}`);
      if (savedTop !== null) {
        const topVal = parseInt(savedTop, 10);
        if (!isNaN(topVal)) {
          mainScrollRef.current.scrollTop = topVal;
          // Clear flags because restoration succeeded
          localStorage.removeItem(`restoreScroll:${parashaKey}`);
          return;
        }
      }
    }
    // Fallback: center the verse element if direct scrollTop not available
    if (restoreFlag) {
      requestAnimationFrame(() => {
        const el =
          verseRefs.current[verseIndex] ||
          document.querySelector(`[data-verse-index="${verseIndex}"]`);
        if (el && mainScrollRef.current) {
          const element = el as HTMLElement;
          const container = mainScrollRef.current;
          const rect = element.getBoundingClientRect();
          const cRect = container.getBoundingClientRect();
          const target =
            container.scrollTop +
            (rect.top - cRect.top) -
            container.clientHeight / 2 +
            rect.height / 2;
          container.scrollTop = Math.max(0, target);
          localStorage.removeItem(`restoreScroll:${parashaKey}`);
        } else if (el) {
          (el as HTMLElement).scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
          localStorage.removeItem(`restoreScroll:${parashaKey}`);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parashaKey, verses.length]);

  // Load latest scores for this parasha
  useEffect(() => {
    if (!parashaKey) return;
    const all = loadScores().filter((s) => s.parasha === parashaKey);
    const bestPersistent = loadBestScores();
    const best: Record<number, number> = {};
    const last: Record<number, { score: number; dateISO: string }> = {};
    for (const entry of all) {
      const verseKey = entry.verseKey;
      const storedBest = bestPersistent[verseKey];
      // Use persistent best if exists else track max
      if (storedBest !== undefined) {
        best[entry.verseIndex] = storedBest;
      } else {
        const prev = best[entry.verseIndex];
        if (prev === undefined || entry.score > prev) best[entry.verseIndex] = entry.score;
      }
      const prevLast = last[entry.verseIndex];
      if (!prevLast || entry.dateISO > prevLast.dateISO) {
        last[entry.verseIndex] = { score: entry.score, dateISO: entry.dateISO };
      }
    }
    setScoresByVerse(best);
    const lastPlain: Record<number, number> = {};
    Object.keys(last).forEach((k) => {
      lastPlain[Number(k)] = last[Number(k)].score;
    });
    setLastScoresByVerse(lastPlain);
  }, [parashaKey, overallScore]);

  // Reset checked verses cache when parasha changes
  useEffect(() => {
    checkedVersesRef.current.clear();
    setUserAudioUrls({});
  }, [parashaKey]);

  // Auto-pause on blur/tab hide
  useEffect(() => {
    const pauseAudio = () => {
      if (audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    };
    const handleVis = () => {
      if (document.hidden) pauseAudio();
    };
    window.addEventListener('blur', pauseAudio);
    window.addEventListener('pagehide', pauseAudio);
    document.addEventListener('visibilitychange', handleVis);
    return () => {
      window.removeEventListener('blur', pauseAudio);
      window.removeEventListener('pagehide', pauseAudio);
      document.removeEventListener('visibilitychange', handleVis);
      pauseAudio();
    };
  }, []);

  const handlePlayUser = async (idx: number) => {
    const blob = recordBlobs[idx];
    const s3Url = userAudioUrls[idx];

    // Need either a local blob or an S3 URL
    if (!blob && !s3Url) return;
    if (!userAudioRef.current) return;

    // If same verse is playing -> pause toggle
    if (isUserPlaying && userPlayingIndex === idx) {
      userAudioRef.current.pause();
      setIsUserPlaying(false);
      setUserPlayingIndex(null);
      trackAudioPlayback('pause', 'user', idx);
      return;
    }
    trackAudioPlayback('play', 'user', idx);
    // Pause rabbi audio if playing
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setPlayingIndex(null);
    }

    // Prefer local blob URL, fall back to S3 URL
    const localUrl = userObjectUrlsRef.current[idx];
    const url = localUrl || s3Url;

    if (url) {
      userAudioRef.current.src = url;
      userAudioRef.current.currentTime = 0;
      try {
        await userAudioRef.current.play();
        setIsUserPlaying(true);
        setUserPlayingIndex(idx);
      } catch (e) {
        console.error('user playback failed', e);
      }
    }
  };

  // Cleanup object URLs on unmount
  useEffect(() => {
    const userAudio = userAudioRef.current;
    return () => {
      Object.values(userObjectUrlsRef.current).forEach((u) => URL.revokeObjectURL(u));
      if (userAudio) userAudio.pause();
      // Cleanup recording interval
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    };
  }, []);

  const [fontMode, setFontMode] = useState<string>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('fontMode') || 'torah' : 'torah',
  );
  const [showFontDropdown, setShowFontDropdown] = useState(false);
  const [fontDropdownPos, setFontDropdownPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const fontButtonRef = useRef<HTMLDivElement>(null);
  const fontDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showFontDropdown) return;
    const handler = (e: MouseEvent) => {
      if (
        fontDropdownRef.current &&
        !fontDropdownRef.current.contains(e.target as Node) &&
        fontButtonRef.current &&
        !fontButtonRef.current.contains(e.target as Node)
      ) {
        setShowFontDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFontDropdown]);

  const toggleFontDropdown = () => {
    if (!showFontDropdown && fontButtonRef.current) {
      const rect = fontButtonRef.current.getBoundingClientRect();
      setFontDropdownPos({ top: rect.bottom + 4, right: rect.right });
    }
    setShowFontDropdown((v) => !v);
  };

  const selectFontMode = (mode: string) => {
    setFontMode(mode);
    localStorage.setItem('fontMode', mode);
    setShowFontDropdown(false);
  };

  // Clicking the text of an already-selected pasuk cycles through the same
  // font modes offered by the top dropdown
  const cycleFontMode = () => {
    const modes = ['torah', 'taamim', 'translit'];
    const next = modes[(modes.indexOf(fontMode) + 1) % modes.length];
    setFontMode(next);
    localStorage.setItem('fontMode', next);
  };
  const textMode = useMemo(
    () => (typeof window !== 'undefined' ? localStorage.getItem('textMode') || 'hebrew' : 'hebrew'),
    [],
  );
  const showTransliteration = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('showTransliteration');
    // If user has explicit preference, use it
    if (stored !== null) return stored !== 'false';
    // Default: show only if user is in English mode
    return i18n.language !== 'he';
  }, [i18n.language]);

  // Memo: group verses according to learning curve setting
  const [versesPerGroup, setVersesPerGroup] = useState<number | 'half' | 'all'>(() => {
    if (_navState.versesPerGroup !== undefined) return _navState.versesPerGroup;
    if (_navState.mode === 'haftara') return 1;
    return getVersesPerGroup();
  });

  // Learning curve option handler
  const handleLearningCurveChange = (option: number | 'half' | 'all') => {
    const keyMap: Record<string, string> = {
      '1': 'single',
      '3': 'triple',
      half: 'half',
      all: 'full',
    };
    const lc: StoredLearningCurve = {
      key: keyMap[String(option)] || 'triple',
      versesCount: option,
    };
    trackLearningCurveChange(option, 'Practice');
    trackSelection('learning_curve', String(option), { parasha: parashaKey });
    setLearningCurve(lc);
    setVersesPerGroup(option);
    setGrouped(option === 3 || option === 'all');
  };
  const selectLearningMode = (mode: LearningMode) => {
    if (mode === learningMode) return;
    if (mode === 'pasuk1') {
      handleLearningCurveChange(1);
    } else if (mode === 'pasuk3') {
      handleLearningCurveChange(3);
    } else if (mode === 'aliyah') {
      handleLearningCurveChange('all');
    } else if (mode === 'haftara') {
      // Haftara is always practiced one pasuk at a time; unlike the other modes
      // this doesn't persist to the user's global learning curve preference.
      setVersesPerGroup(1);
      setGrouped(false);
    }
    setLearningMode(mode);
  };

  const groupedVerses = useMemo(() => {
    if (!grouped) {
      return verses.map((v, idx) => ({ verses: [v], startIndex: idx }));
    }
    const arr: { verses: TorahVerse[]; startIndex: number }[] = [];

    // Handle different grouping modes
    if (versesPerGroup === 'all') {
      // All verses in one group
      if (verses.length > 0) {
        arr.push({ verses: [...verses], startIndex: 0 });
      }
    } else if (versesPerGroup === 'half') {
      // Split into two halves
      const halfPoint = Math.ceil(verses.length / 2);
      if (halfPoint > 0) {
        arr.push({ verses: verses.slice(0, halfPoint), startIndex: 0 });
      }
      if (halfPoint < verses.length) {
        arr.push({ verses: verses.slice(halfPoint), startIndex: halfPoint });
      }
    } else {
      // Group by specific number (1 or 3)
      const groupSize = typeof versesPerGroup === 'number' ? versesPerGroup : 3;
      for (let i = 0; i < verses.length; i += groupSize) {
        arr.push({ verses: verses.slice(i, i + groupSize), startIndex: i });
      }
    }
    return arr;
  }, [grouped, verses, versesPerGroup]);

  // Lazy-load user recordings check via IntersectionObserver
  // Only check recordings for verses that enter the viewport
  useEffect(() => {
    if (!verses.length) return;

    // Batch check function - debounced to avoid too many API calls
    const batchCheckRecordings = () => {
      if (pendingCheckRef.current.size === 0) return;
      const indicesToCheck = Array.from(pendingCheckRef.current);
      pendingCheckRef.current.clear();
      checkExistingRecordings(indicesToCheck);
    };

    // Create IntersectionObserver to detect when verse elements enter viewport
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const verseIdx = parseInt((entry.target as HTMLElement).dataset.verseIndex || '-1', 10);
            if (verseIdx >= 0 && !checkedVersesRef.current.has(verseIdx)) {
              // Add to pending batch
              pendingCheckRef.current.add(verseIdx);
              // For grouped mode, also add all verses in the group
              if (grouped) {
                const group = groupedVerses.find(
                  (g) => verseIdx >= g.startIndex && verseIdx < g.startIndex + g.verses.length,
                );
                if (group) {
                  group.verses.forEach((_, i) => {
                    const idx = group.startIndex + i;
                    if (!checkedVersesRef.current.has(idx)) {
                      pendingCheckRef.current.add(idx);
                    }
                  });
                }
              }
              // Debounce the batch check (wait 150ms for more entries)
              if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
              }
              checkTimeoutRef.current = setTimeout(batchCheckRecordings, 150);
            }
          }
        });
      },
      {
        root: mainScrollRef.current,
        rootMargin: '100px', // Start loading slightly before entering viewport
        threshold: 0.1,
      },
    );

    // Observe all verse elements
    Object.values(verseRefs.current).forEach((el) => {
      if (el && observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (checkTimeoutRef.current) {
        clearTimeout(checkTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verses, grouped, groupedVerses]);

  // Listen for external changes (ProfilePage) to grouped setting
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === 'groupedVerses') {
        setGrouped(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Helper: determine which word index should be active given an alignment and a time (seconds)
  const getActiveWordIndex = (
    alignment: { start: number | string; end: number | string }[],
    t: number,
  ): number => {
    if (!alignment || alignment.length === 0) return -1;
    // Use sanitized alignment to avoid jumping to last word
    const norm = sanitizeAlignment(alignment as any);
    if (norm.length === 0) return -1;
    const first = norm[0];
    const last = norm[norm.length - 1];

    // Highlight first at very beginning or before its start
    if (t <= first.start + 0.01) return 0;

    // Find active word based on current time
    for (let i = 0; i < norm.length; i++) {
      const w = norm[i];
      if (t >= w.start && t < w.end) return i;
    }

    // After last word end keep last
    if (t >= last.end) return norm.length - 1;
    return -1;
  };

  // Lazy fetch alignment for selected verse when verseIndex changes (if not already cached)
  useEffect(() => {
    const v = verses[verseIndex];
    if (!v) return;
    if (alignmentsByVerse[verseIndex]) return; // already loaded
    // Fetch alignment only upon selection
    fetchAlignments(v).catch((e) =>
      console.error('Failed fetching alignment for verse', verseIndex, e),
    );
    // rabbi deps: retry once the default rabbi for the nusach is resolved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verseIndex, verses, alignmentsByVerse, parashaRabbi, haftaraRabbi]);

  // Attach timeupdate listener to update current word when playing selected verse (karaoke for single or group)
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    const onTimeUpdate = () => {
      if (!isPlaying || playingIndex === null) {
        if (!isRecording && currentWordIndex !== null) {
          // keep first word highlight when paused at 0 due to restart
          if (audioEl.currentTime === 0) {
            setCurrentWordIndex(0);
          } else {
            setCurrentWordIndex(null);
          }
          setCurrentVerseInPlaybackGroup(null);
        }
        setPlaybackTime(audioEl.currentTime || 0);
        return;
      }
      const alignment = alignmentsByVerse[playingIndex];
      if (!alignment || alignment.length === 0) {
        if (!isRecording && currentWordIndex !== null) {
          setCurrentWordIndex(null);
          setCurrentVerseInPlaybackGroup(null);
        }
        setPlaybackTime(audioEl.currentTime || 0);
        return;
      }
      const tSec = audioEl.currentTime;
      setPlaybackTime(tSec);
      const active = getActiveWordIndex(alignment as any, tSec);
      let versePositionInGroup = 0;
      if (grouped) {
        const group = groupedVerses.find(
          (g) => playingIndex >= g.startIndex && playingIndex < g.startIndex + g.verses.length,
        );
        if (group) {
          versePositionInGroup = group.verses.findIndex(
            (_, i) => group.startIndex + i === playingIndex,
          );
        }
      }
      if (!isRecording) {
        setCurrentVerseInPlaybackGroup(versePositionInGroup);
        setCurrentWordIndex(active);
      }
    };
    const onEnded = () => {
      if (!isRecording) {
        /* waiting for next verse or end */
      }
    };
    audioEl.addEventListener('timeupdate', onTimeUpdate);
    audioEl.addEventListener('ended', onEnded);
    audioEl.addEventListener('loadedmetadata', () => {
      setPlaybackDuration(audioEl.duration || 0);
    });
    return () => {
      audioEl.removeEventListener('timeupdate', onTimeUpdate);
      audioEl.removeEventListener('ended', onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    playingIndex,
    isPlaying,
    groupPlaybackQueue.length,
    verseIndex,
    alignmentsByVerse,
    currentWordIndex,
    isRecording,
    grouped,
    groupedVerses,
  ]);

  // NEW: Karaoke mode during recording - update word highlighting based on elapsed recording time
  useEffect(() => {
    if (!isRecording || recordingIndex === null) {
      // Clean up interval if recording stops
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      return;
    }

    // Determine target indices (single verse or group)
    let targetIndices: number[] = [recordingIndex];
    if (grouped) {
      const group = groupedVerses.find(
        (g) => recordingIndex >= g.startIndex && recordingIndex < g.startIndex + g.verses.length,
      );
      if (group) targetIndices = group.verses.map((_, i) => group.startIndex + i);
    }

    // Check if we have alignments for at least the first verse
    const firstAlignment = alignmentsByVerse[targetIndices[0]];
    if (!firstAlignment || firstAlignment.length === 0) {
      // No alignment data available, can't do karaoke
      return;
    }

    // Calculate cumulative durations for each verse in the group
    const verseDurations: number[] = [];
    let cumulativeTime = 0;
    for (const verseIdx of targetIndices) {
      const alignment = alignmentsByVerse[verseIdx];
      if (alignment && alignment.length > 0) {
        const lastWord = alignment[alignment.length - 1] as { end: number };
        const verseDuration = lastWord?.end || 0;
        verseDurations.push(cumulativeTime);
        cumulativeTime += verseDuration;
      } else {
        verseDurations.push(cumulativeTime);
      }
    }

    // Start interval to update word highlighting
    recordingIntervalRef.current = setInterval(() => {
      if (!recordingStartTimeRef.current) return;

      const elapsedSeconds = (Date.now() - recordingStartTimeRef.current) / 1000;

      // Find which verse we're currently in
      let currentVerseIdx = 0;
      let active = -1;

      for (let vIdx = 0; vIdx < targetIndices.length; vIdx++) {
        const verseIdx = targetIndices[vIdx];
        const alignment = alignmentsByVerse[verseIdx];
        if (!alignment || alignment.length === 0) continue;

        const verseStartTime = verseDurations[vIdx];
        const lastWord = alignment[alignment.length - 1] as { end: number };
        const verseEndTime = verseStartTime + (lastWord?.end || 0);

        // Check if we're in this verse's time range
        if (elapsedSeconds >= verseStartTime && elapsedSeconds < verseEndTime) {
          currentVerseIdx = vIdx;
          // Calculate relative time within this verse
          const relativeTime = elapsedSeconds - verseStartTime;
          active = getActiveWordIndex(alignment as any, relativeTime);
          break;
        } else if (elapsedSeconds >= verseEndTime && vIdx === targetIndices.length - 1) {
          // Past the last verse, highlight last word of last verse
          currentVerseIdx = vIdx;
          active = alignment.length - 1;
        }
      }

      setCurrentVerseInGroup(currentVerseIdx);
      setCurrentWordIndex(active);
    }, 50); // Update every 50ms for smooth highlighting

    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, recordingIndex, alignmentsByVerse, grouped, groupedVerses]);

  if (!selectedParasha) {
    return (
      <div
        data-aid="practice-no-parasha-container"
        className="mx-auto max-w-[420px] p-8 rounded-xl shadow-lg bg-white/80 dark:bg-zinc-900/80 border border-zinc-200 dark:border-zinc-800 flex flex-col items-center"
      >
        <div className="flex items-center mb-4">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-amber-600 text-white shadow-lg mr-3 text-2xl">
            <svg
              viewBox="0 0 24 24"
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                d="M12 9v2m0 4h.01M21 21L3 3m18 0L3 21"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <h2
            data-aid="practice-no-parasha-title"
            className="text-xl font-bold text-zinc-800 dark:text-zinc-100 m-0"
          >
            No Parasha Selected
          </h2>
        </div>
        <p
          data-aid="practice-no-parasha-message"
          className="mb-6 text-zinc-500 dark:text-zinc-400 text-base"
        >
          Please choose a parasha to begin practicing. It looks like you haven't selected one yet.
        </p>
        <a
          data-aid="practice-no-parasha-go-to-start-link"
          href="/aliya-latora"
          className="inline-block bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold px-5 py-2 rounded-md shadow-lg hover:from-blue-600 hover:to-indigo-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          Go to Start
        </a>
      </div>
    );
  }

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // result is a data URL: data:<mime>;base64,<data>
          const base64 = result.split(',')[1] || '';
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      } catch (err) {
        reject(err);
      }
    });
  };

  // Get user ID from localStorage (same userId as in users collection for subscriptions)
  const getUserId = (): string | null => {
    try {
      const storedUser = localStorage.getItem('loggedInUser');
      if (storedUser) {
        const parsed = JSON.parse(storedUser) as {
          userId?: string;
          uid?: string;
          email?: string;
        };
        return parsed.userId ?? parsed.uid ?? parsed.email ?? null;
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  // Upload user recording to S3
  const uploadUserRecording = async (verseIdx: number, blob: Blob): Promise<string | null> => {
    const userId = getUserId();
    if (!userId) {
      console.warn('No user ID available for uploading recording');
      return null;
    }

    const verse = verses[verseIdx];
    if (!verse) return null;

    try {
      const base64Audio = await blobToBase64(blob);
      const response = await callCloudFunction(
        'uploadUserAudio',
        FIREBASE_FUNCTIONS.uploadUserAudio(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            book: verse.book,
            chapter: verse.chapter,
            pasuk: verse.pasuk,
            audioBase64: base64Audio,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        console.log('User recording uploaded:', data.url);
        return data.url || null;
      } else {
        console.error('Failed to upload user recording:', await response.text());
      }
    } catch (err) {
      console.error('Error uploading user recording:', err);
    }
    return null;
  };

  // Check for existing user recordings in S3
  const checkExistingRecordings = async (verseIndices: number[]) => {
    const userId = getUserId();
    if (!userId) return;

    // Filter out already checked verses
    const uncheckedIndices = verseIndices.filter((idx) => !checkedVersesRef.current.has(idx));
    if (uncheckedIndices.length === 0) return;

    // Build psukim array for API call
    const psukim = uncheckedIndices
      .map((idx) => {
        const verse = verses[idx];
        return {
          id: String(idx),
          book: verse?.book,
          chapter: verse?.chapter,
          pasuk: verse?.pasuk,
        };
      })
      .filter((p) => p.book && p.chapter && p.pasuk);

    if (psukim.length === 0) return;

    try {
      const response = await callCloudFunction(
        'checkUserAudioExists',
        FIREBASE_FUNCTIONS.checkUserAudioExists(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, psukim }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        const existence = data.existence as Record<string, { exists: boolean; url?: string }>;

        // Update userAudioUrls state with found URLs
        const newUrls: Record<number, string> = {};
        Object.entries(existence).forEach(([idStr, info]) => {
          const idx = parseInt(idStr, 10);
          if (info.exists && info.url) {
            newUrls[idx] = info.url;
          }
          // Mark as checked
          checkedVersesRef.current.add(idx);
        });

        if (Object.keys(newUrls).length > 0) {
          setUserAudioUrls((prev) => ({ ...prev, ...newUrls }));
        }
      }
    } catch (err) {
      console.error('Error checking existing recordings:', err);
    }
  };

  const handleShare = async (idx: number) => {
    trackPracticeAction('share', idx, { parasha: parashaKey });
    trackClick('Share', 'Practice', { verse_index: idx });
    let blob = recordBlobs[idx];
    const s3Url = userAudioUrls[idx];
    if (!blob && s3Url) {
      try {
        const resp = await fetch(s3Url);
        if (resp.ok) blob = await resp.blob();
      } catch {
        /* fall through */
      }
    }
    if (!blob) return;
    const globalIdx = verseGlobalIndices[idx] ?? idx;
    const score = lastScoresByVerse[globalIdx] ?? scoresByVerse[globalIdx] ?? 0;
    const verseNum = idx + 1;
    const ext = audioMimeToExtension(blob.type);
    const fileName = `parasha_${parashaKey || 'unknown'}_verse_${verseNum}.${ext}`;
    const file = new File([blob], fileName, { type: blob.type });
    const text =
      t('share_text', {
        verse: formatNumberForLocale(verseNum, i18n.language || ''),
        score,
      }) || `Verse ${verseNum} score: ${score}%`;
    try {
      const x: any = window as any;
      if (x.flutter_inappwebview) {
        try {
          const base64 = await blobToBase64(blob);
          await x.flutter_inappwebview.callHandler('nativeShare', {
            text,
            fileName,
            mimeType: blob.type || 'audio/wav',
            base64,
            size: blob.size,
            verse: verseNum,
            parasha: parashaKey || 'unknown',
            score,
          });
        } catch (convErr) {
          console.error('Failed converting blob to base64 for sharing', convErr);
          await x.flutter_inappwebview.callHandler('nativeShare', { text });
        }
        return;
      }
      if ((navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
        await (navigator as any).share({
          files: [file],
          title: t('share_title') || 'My Torah Practice',
          text,
        });
      } else {
        try {
          await (navigator as any).share({
            title: t('share_title') || 'My Torah Practice',
            text,
          });
        } catch {
          try {
            await navigator.clipboard.writeText(text);
            alert(
              (t('copied_to_clipboard') || 'Copied text to clipboard') +
                '\n' +
                (t('share_failed_no_api') || 'File sharing not supported.'),
            );
          } catch {
            alert(t('share_failed') || 'Share failed.');
          }
        }
      }
    } catch (e) {
      console.error('share failed', e);
      alert(t('share_failed') || 'Share failed.');
    }
  };

  const restartSinglePlayback = async (idx: number) => {
    if (!audioRef.current) return;
    const verse = verses[idx];
    if (!verse) return;
    if (!alignmentsByVerse[idx]) {
      await fetchAlignments(verse).catch((e) =>
        console.error('fetch alignment failed before restart', e),
      );
    }
    // Do NOT auto-play, just seek to start
    try {
      const audioEl = audioRef.current;
      const rabbiUrl = getRabbiAudioUrl(verse, nusach, getRabiInitials());
      if (lastRabbiUrlRef.current !== rabbiUrl || audioEl.src === '') {
        audioEl.src = rabbiUrl;
        audioEl.load();
        lastRabbiUrlRef.current = rabbiUrl;
      }
      audioEl.pause();
      audioEl.currentTime = 0;
      setPlaybackTime(0);
      // Keep verse selected for potential play
      setPlayingIndex(idx);
      setIsPlaying(false);
      // Reset marker to first word visually
      setCurrentWordIndex(0);
      setCurrentVerseInPlaybackGroup(0);
    } catch (e) {
      console.error('restart single playback failed', e);
    }
  };

  const restartGroupPlayback = async (groupIndices: number[]) => {
    if (!audioRef.current || groupIndices.length === 0) return;
    for (const verseIdx of groupIndices) {
      const verse = verses[verseIdx];
      if (verse && !alignmentsByVerse[verseIdx]) {
        await fetchAlignments(verse).catch((e) =>
          console.error('Failed fetching alignment for group restart', verseIdx, e),
        );
      }
    }
    // Pause & seek to start of first verse; do not auto-play; clear queue
    const firstIdx = groupIndices[0];
    try {
      const verse = verses[firstIdx];
      const audioEl = audioRef.current;
      const rabbiUrl = getRabbiAudioUrl(verse, nusach, getRabiInitials());
      audioEl.pause();
      if (lastRabbiUrlRef.current !== rabbiUrl || audioEl.src === '') {
        audioEl.src = rabbiUrl;
        audioEl.load();
        lastRabbiUrlRef.current = rabbiUrl;
      }
      audioEl.currentTime = 0;
      setPlaybackTime(0);
      setPlayingIndex(firstIdx);
      setIsPlaying(false);
      setGroupPlaybackQueue([]); // reset queue; user pressing Play Group will rebuild
      setCurrentWordIndex(0);
      setCurrentVerseInPlaybackGroup(0);
    } catch (e) {
      console.error('restart group playback failed', e);
    }
  };

  return (
    <div
      data-aid="practice-page-container"
      className="flex flex-col mx-auto max-w-[980px] flex-grow bg-[#F9FAFB]"
      style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      {celebration && (
        <ScoreCelebrationCheckmark
          key={`${celebration.idx}-${celebration.nonce}`}
          targetRef={getScoreBadgeRef(celebration.idx)}
          originRef={getPasukContainerRef(celebration.idx)}
          triggered
          onComplete={() => setCelebration(null)}
        />
      )}
      <AIConsentDialog
        open={showAIConsentDialog}
        onAgree={() => {
          setAIConsent();
          setShowAIConsentDialog(false);
          const idx = pendingCompareIndex;
          setPendingCompareIndex(null);
          if (idx !== null) handleCompare(idx);
        }}
        onCancel={() => {
          setShowAIConsentDialog(false);
          setPendingCompareIndex(null);
        }}
      />
      {/* Guest login popup */}
      {showGuestLoginPopup && (
        <div
          data-aid="guest-login-popup-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowGuestLoginPopup(false)}
        >
          <div
            data-aid="guest-login-popup"
            style={{
              background: '#FDF9F3',
              borderRadius: 20,
              padding: '28px 24px',
              maxWidth: 320,
              width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              direction: i18n.language?.startsWith('he') ? 'rtl' : 'ltr',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 36 }}>🔒</div>
            <p
              style={{
                textAlign: 'center',
                fontSize: 15,
                fontWeight: 600,
                color: TEXT_DARK,
                margin: 0,
              }}
            >
              {i18n.language?.startsWith('he')
                ? 'כדי להשתמש בהשוואה, יש להתחבר לחשבון'
                : 'Sign in to use the Compare feature'}
            </p>
            <p style={{ textAlign: 'center', fontSize: 13, color: TEXT_SECONDARY, margin: 0 }}>
              {i18n.language?.startsWith('he') ? 'התחברות חינם, ללא תשלום' : 'Signing in is free'}
            </p>
            <button
              data-aid="guest-login-signin-button"
              onClick={() => {
                setShowGuestLoginPopup(false);
                navigate('/onboarding', { state: { returnTo: '/practice' } });
              }}
              style={{
                width: '100%',
                padding: '12px 0',
                borderRadius: 9999,
                background: GOLD_BUTTON,
                border: 'none',
                color: TEXT_DARK,
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {i18n.language?.startsWith('he') ? 'כניסה / הרשמה' : 'Log in / Sign up'}
            </button>
            <button
              data-aid="guest-login-cancel-button"
              onClick={() => setShowGuestLoginPopup(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: TEXT_SECONDARY,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {i18n.language?.startsWith('he') ? 'ביטול' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
      {/* Compare limit reached popup (unpaid users) */}
      {showCompareLimitPopup && (
        <div
          data-aid="compare-limit-popup-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowCompareLimitPopup(false)}
        >
          <div
            data-aid="compare-limit-popup"
            style={{
              background: '#FDF9F3',
              borderRadius: 20,
              padding: '28px 24px',
              maxWidth: 320,
              width: '90%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 16,
              direction: i18n.language?.startsWith('he') ? 'rtl' : 'ltr',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: 36 }}>⭐</div>
            <p
              style={{
                textAlign: 'center',
                fontSize: 15,
                fontWeight: 600,
                color: TEXT_DARK,
                margin: 0,
              }}
            >
              {i18n.language?.startsWith('he')
                ? `ניצלת את ${FREE_COMPARE_LIMIT} ההשוואות החינמיות`
                : `You've used all ${FREE_COMPARE_LIMIT} free comparisons`}
            </p>
            <p style={{ textAlign: 'center', fontSize: 13, color: TEXT_SECONDARY, margin: 0 }}>
              {i18n.language?.startsWith('he')
                ? 'שדרג לגישה מלאה להמשך תרגול ומעקב'
                : 'Upgrade for unlimited comparisons and full access'}
            </p>
            <CompareLimitUpgradeButton
              isHebrew={!!i18n.language?.startsWith('he')}
              userEmail={userEmail}
              onDone={() => setShowCompareLimitPopup(false)}
            />
            <button
              data-aid="compare-limit-cancel-button"
              onClick={() => setShowCompareLimitPopup(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: TEXT_SECONDARY,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {i18n.language?.startsWith('he') ? 'ביטול' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
      {/* Slow blink animation for new results */}
      <style>
        {`
          @keyframes slowBlink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.05); }
          }
        `}
      </style>

      {/* Row 2: filter chips */}
      <div
        data-aid="practice-filters-bar"
        className="flex items-center gap-2 px-3 py-2 bg-[#F9FAFB] border-b shrink-0 justify-center"
        style={{
          overflowX: 'auto',
          flexWrap: 'nowrap',
          scrollbarWidth: 'none',
          justifyContent: 'space-between',
        }}
        dir={i18n.language?.startsWith('he') ? 'rtl' : 'ltr'}
      >
        {/* Font mode dropdown */}
        <div data-aid="font-mode-dropdown" style={{ flexShrink: 0 }}>
          <div
            data-aid="font-mode-dropdown-button"
            ref={fontButtonRef}
            onClick={toggleFontDropdown}
            className="py-1 text-sm font-medium rounded-lg border transition-all"
            style={{
              background: 'white',
              borderColor: BORDER_LIGHT,
              color: TEXT_DARK,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '4px 8px',
            }}
            aria-label={t('font_mode') || 'Font style'}
          >
            {fontMode === 'translit' ? (
              <span style={{ fontFamily: 'sans-serif', fontSize: 11 }}>abc</span>
            ) : fontMode === 'torah' ? (
              <span className="font-torah">אב</span>
            ) : fontMode === 'taamim' ? (
              <span style={{ fontFamily: 'sans-serif' }}>אָ֨ב֘</span>
            ) : (
              <span style={{ fontFamily: 'sans-serif' }}>אב</span>
            )}
            <span style={{ fontSize: 18, lineHeight: 1 }}>▾</span>
          </div>
          {showFontDropdown && (
            <div
              data-aid="font-mode-dropdown-menu"
              ref={fontDropdownRef}
              style={{
                position: 'fixed',
                top: fontDropdownPos.top,
                zIndex: 9999,
                background: 'white',
                border: `1px solid ${BORDER_LIGHT}`,
                borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                minWidth: 130,
                overflow: 'hidden',
              }}
            >
              {(
                [
                  {
                    key: 'torah',
                    preview: <span className="font-torah">אבגד</span>,
                  },
                  {
                    key: 'taamim',
                    preview: <span style={{ fontFamily: 'sans-serif' }}>אָ֨בָ֘גד</span>,
                  },
                  {
                    key: 'translit',
                    preview: <span style={{ fontFamily: 'sans-serif', fontSize: 11 }}>abcde</span>,
                  },
                ] as { key: string; preview: React.ReactNode }[]
              ).map(({ key, preview }) => (
                <div
                  key={key}
                  data-aid={`font-mode-option-${key}`}
                  onClick={() => selectFontMode(key)}
                  style={{
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'center',
                    width: '100%',
                    padding: '8px',
                    background: fontMode === key ? '#F3F4F6' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: TEXT_DARK,
                    fontWeight: fontMode === key ? 600 : 400,
                  }}
                >
                  {preview}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Parasha chip */}
        <div
          data-aid="parasha-chip"
          className="flex-shrink-0"
          style={{ position: 'relative', display: 'inline-flex' }}
        >
          <div
            data-aid="parasha-chip-label"
            className="flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full border"
            style={{
              borderColor: BORDER_LIGHT,
              color: TEXT_DARK,
              background: 'white',
              whiteSpace: 'nowrap',
            }}
          >
            {i18n.language?.startsWith('he') ? selectedParasha.hebrew : selectedParasha.english}
            {/*<span style={{ fontSize: 9 }}>▼</span>*/}
          </div>
          {/*<ParashaChooser*/}
          {/*  value={selectedParasha?.english ?? ''}*/}
          {/*  onChange={handleParashaSelect}*/}
          {/*  variant="compact"*/}
          {/*  style={{*/}
          {/*    position: 'absolute',*/}
          {/*    inset: 0,*/}
          {/*    opacity: 0,*/}
          {/*    cursor: 'pointer',*/}
          {/*    width: '100%',*/}
          {/*    height: '100%',*/}
          {/*  }}*/}
          {/*/>*/}
        </div>

        {/* Learning mode chip (transparent select overlay) */}
        <div
          data-aid="learning-mode-chip"
          className="flex-shrink-0"
          style={{ position: 'relative', display: 'inline-flex' }}
        >
          <div
            data-aid="learning-mode-chip-label"
            className="flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full border"
            style={{
              borderColor: BORDER_LIGHT,
              color: TEXT_DARK,
              background: 'white',
              whiteSpace: 'nowrap',
            }}
          >
            {learningMode === 'pasuk1'
              ? i18n.language?.startsWith('he')
                ? 'פסוק בודד'
                : '1 Pasuk'
              : learningMode === 'pasuk3'
                ? i18n.language?.startsWith('he')
                  ? '3 פסוקים'
                  : '3 Psukim'
                : learningMode === 'haftara'
                  ? i18n.language?.startsWith('he')
                    ? 'הפטרה'
                    : 'Haftara'
                  : i18n.language?.startsWith('he')
                    ? 'עלייה מלאה'
                    : 'Full Aliyah'}
            <span style={{ fontSize: 9 }}>▼</span>
          </div>
          <select
            data-aid="learning-mode-select"
            value={learningMode === 'aliyah' ? 'aliyah-full' : learningMode}
            onChange={(e) => {
              const val = e.target.value;
              if (val === 'pasuk1') selectLearningMode('pasuk1');
              else if (val === 'pasuk3') selectLearningMode('pasuk3');
              else if (val === 'haftara') selectLearningMode('haftara');
              else if (val === 'aliyah-full') {
                selectLearningMode('aliyah');
              }
            }}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}
            aria-label={t('choose_learning_curve') || 'Choose learning pace'}
          >
            {learningCurveOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <main
        data-aid="practice-main-content"
        style={{
          flexGrow: 1,
          boxSizing: 'border-box',
          display: 'flex',
          overflowY: 'auto',
          flexDirection: 'column',
          alignItems: 'center',
          fontFamily: 'system-ui, sans-serif',
        }}
        className="px-4 overflow-y-auto flex-grow"
        ref={mainScrollRef}
      >
        <section className="mb-4">
          {/* Progress badge */}
          <div
            data-aid="practice-progress-badge"
            style={{
              background: 'rgba(59, 123, 244, 0.12)',
              color: BLUE_ACCENT,
              borderRadius: 999,
              padding: '3px 12px',
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              lineHeight: 1.7,
              justifySelf: 'center',
              margin: '10px 0',
            }}
          >
            {i18n.language?.startsWith('he')
              ? `הושלמו ${progressPercent}% פסוק ${numberToHebrewLetters(verseIndex + 1)} מתוך ${numberToHebrewLetters(verses.length || 1)}`
              : `${progressPercent}% · Pasuk ${verseIndex + 1} of ${verses.length}`}
          </div>

          <div className="pr-1 space-y-3">
            <ul data-aid="verse-groups-list" className="space-y-3">
              {/* UPDATED: render groups instead of individual verses */}
              {groupedVerses.map((group) => {
                const { verses: groupItems, startIndex } = group;
                // Determine if selected verse is inside this group
                const selectedInGroup =
                  verseIndex >= startIndex && verseIndex < startIndex + groupItems.length;
                return (
                  <li
                    key={startIndex}
                    data-aid={`verse-group-${startIndex}`}
                    className="rounded-2xl"
                  >
                    <div
                      ref={getPasukContainerRef(startIndex)}
                      data-aid={`verse-group-card-${startIndex}`}
                      className="w-full rounded-2xl px-3 py-3 text-sm transition-colors bg-white cursor-pointer"
                      style={{
                        border: selectedInGroup
                          ? `2px solid ${BLUE_ACCENT}`
                          : `1px solid ${BORDER_LIGHT}`,
                        boxShadow: selectedInGroup ? '0 4px 12px rgba(59, 123, 244, 0.15)' : 'none',
                      }}
                      onClick={() => setVerseIndex(startIndex)}
                    >
                      {/* Single pasuk mode - render each verse separately */}
                      {versesPerGroup === 1 ? (
                        <div data-aid="single-verse-list" className="flex flex-col gap-4">
                          {groupItems.map((v, localIdx) => {
                            const idx = startIndex + localIdx;
                            const selected = idx === verseIndex;
                            const globalIdx = verseGlobalIndices[idx];
                            const score =
                              globalIdx !== undefined ? scoresByVerse[globalIdx] : undefined;
                            const chapterNum = v.chapter ?? 0;
                            const pasukNum = v.pasuk ?? 0;
                            const isHeb = i18n.language?.startsWith('he');
                            const chapterDisplay = isHeb
                              ? numberToHebrewLetters(chapterNum)
                              : String(chapterNum);
                            const pasukDisplay = isHeb
                              ? numberToHebrewLetters(pasukNum)
                              : String(pasukNum);
                            // When transliteration is requested but missing for this verse,
                            // we fall back to Hebrew text, so the verse must render RTL/right-aligned
                            // instead of inheriting the transliteration layout's LTR/left alignment.
                            const showsHebrewText =
                              (textMode === 'hebrew' && fontMode !== 'translit') ||
                              !v.transliteration;
                            return (
                              <div
                                key={v.id || idx}
                                data-aid={`verse-item-${idx}`}
                                data-verse-index={idx}
                                ref={(el) => {
                                  verseRefs.current[idx] = el;
                                  // Observe element for lazy-loading recordings
                                  if (el && observerRef.current) {
                                    observerRef.current.observe(el);
                                  }
                                }}
                                className="rounded-xl p-3"
                                style={{
                                  background: selected ? 'rgba(59, 123, 244, 0.04)' : 'transparent',
                                }}
                              >
                                <div
                                  className="flex items-center gap-3"
                                  dir={i18n.language?.startsWith('he') ? 'rtl' : 'ltr'}
                                >
                                  <button
                                    data-aid={`verse-select-button-${idx}`}
                                    onClick={() => setVerseIndex(idx)}
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold text-white"
                                    style={{
                                      background: selected ? BLUE_ACCENT : TEXT_DARK,
                                    }}
                                    aria-label={t('select_verse') || 'Select verse'}
                                  >
                                    {formatNumberForLocale(idx + 1, i18n.language || '')}
                                  </button>
                                  <span
                                    data-aid={`verse-label-${idx}`}
                                    className="flex-1"
                                    onClick={() => setVerseIndex(idx)}
                                  >
                                    {t('verse_label_full', {
                                      chapter: chapterDisplay,
                                      pasuk: pasukDisplay,
                                    })}
                                  </span>
                                  {score !== undefined && (
                                    <span
                                      data-aid={`verse-best-score-${idx}`}
                                      className="text-[11px] text-[#0d8f4d] font-medium"
                                    >
                                      {t('best_score') || 'Best score:'} {score}%
                                    </span>
                                  )}
                                </div>
                                <div
                                  data-aid={`verse-text-${idx}`}
                                  dir={showsHebrewText ? 'rtl' : 'ltr'}
                                  className={
                                    'mt-2 ' +
                                    (showsHebrewText ? 'text-right' : 'text-left') +
                                    ' ' +
                                    (fontMode === 'torah' ? 'font-torah' : '')
                                  }
                                  style={{
                                    color: TEXT_DARK,
                                    fontSize: '1.125rem',
                                    lineHeight: '2',
                                    letterSpacing: 0,
                                    ...(fontMode !== 'torah'
                                      ? {
                                          fontFamily: '"Noto Serif Hebrew", "David Libre", serif',
                                          fontSynthesis: 'none',
                                          WebkitFontSmoothing: 'antialiased',
                                        }
                                      : {}),
                                  }}
                                  onClick={(e) => {
                                    if (idx === verseIndex) {
                                      e.stopPropagation();
                                      cycleFontMode();
                                    } else {
                                      setVerseIndex(idx);
                                    }
                                  }}
                                >
                                  {(() => {
                                    // Karaoke support for single pasuk mode
                                    const shouldShowKaraoke =
                                      (playingIndex !== null &&
                                        idx === playingIndex &&
                                        alignmentsByVerse[idx]) ||
                                      (isRecording &&
                                        recordingIndex === idx &&
                                        alignmentsByVerse[idx]);

                                    if (textMode === 'hebrew' && shouldShowKaraoke) {
                                      if (fontMode === 'translit') {
                                        // Karaoke in translit mode: highlight transliteration words
                                        const translitWords = (v.transliteration ?? '')
                                          .split(/\s+/)
                                          .filter(Boolean);
                                        // Some verses (e.g. haftara) have no transliteration data;
                                        // fall through to the Hebrew word rendering below instead of
                                        // showing a blank verse.
                                        if (translitWords.length > 0) {
                                          return translitWords.map((word: string, wIdx: number) => {
                                            const isHighlighted =
                                              (playingIndex !== null &&
                                                idx === playingIndex &&
                                                currentWordIndex === wIdx) ||
                                              (isRecording &&
                                                recordingIndex === idx &&
                                                currentWordIndex === wIdx);
                                            return (
                                              <span
                                                key={wIdx}
                                                style={{
                                                  color: isHighlighted ? 'red' : undefined,
                                                  transition: 'color 0.15s linear',
                                                }}
                                              >
                                                {word}{' '}
                                              </span>
                                            );
                                          });
                                        }
                                      }
                                      // Use words from torah.json (v.hebrew) for accurate display
                                      const verseWords = v.hebrew
                                        .replace(/־/g, ' ')
                                        .split(/\s+/)
                                        .filter(Boolean);
                                      return verseWords.map((word: string, wIdx: number) => {
                                        const isHighlighted =
                                          (playingIndex !== null &&
                                            idx === playingIndex &&
                                            currentWordIndex === wIdx) ||
                                          (isRecording &&
                                            recordingIndex === idx &&
                                            currentWordIndex === wIdx);
                                        return (
                                          <span
                                            key={wIdx}
                                            style={{
                                              color: isHighlighted ? 'red' : undefined,
                                              transition: 'color 0.15s linear',
                                            }}
                                          >
                                            {fontMode !== 'torah'
                                              ? renderHebrewWithShvaNa(word)
                                              : formatHebrewText(word)}{' '}
                                          </span>
                                        );
                                      });
                                    }
                                    // Fall back to the Hebrew text when transliteration data is
                                    // missing for this verse (e.g. haftara verses), so we never
                                    // render a blank verse.
                                    return textMode === 'hebrew'
                                      ? fontMode === 'translit'
                                        ? (v.transliteration ?? renderHebrewWithShvaNa(v.hebrew))
                                        : fontMode !== 'torah'
                                          ? renderHebrewWithShvaNa(v.hebrew)
                                          : formatHebrewText(v.hebrew)
                                      : (v.transliteration ?? v.hebrew);
                                  })()}
                                </div>
                                {textMode === 'hebrew' && showTransliteration ? (
                                  <div
                                    data-aid={`verse-transliteration-box-${idx}`}
                                    className="bg-gray-50 rounded-xl p-2 text-xs mt-2"
                                  >
                                    <div
                                      className="text-[10px] font-semibold mb-1"
                                      style={{ color: TEXT_SECONDARY }}
                                    >
                                      {t('transliteration_text') || 'Transliteration'}
                                    </div>
                                    <div style={{ color: TEXT_DARK }}>{v.transliteration}</div>
                                  </div>
                                ) : textMode === 'translit' ? (
                                  <div
                                    data-aid={`verse-hebrew-box-${idx}`}
                                    className="bg-gray-50 rounded-xl p-2 text-xs mt-2"
                                    dir="rtl"
                                  >
                                    <div
                                      className="text-[10px] font-semibold mb-1"
                                      style={{ color: TEXT_SECONDARY }}
                                    >
                                      {t('hebrew_text') || 'Hebrew'}
                                    </div>
                                    <div style={{ color: TEXT_DARK }}>
                                      {formatHebrewText(v.hebrew)}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        /* Grouped mode - combine all verses' text together */
                        <div
                          data-aid={`verse-group-text-${startIndex}`}
                          data-verse-index={startIndex}
                          ref={(el) => {
                            verseRefs.current[startIndex] = el;
                            // Observe element for lazy-loading recordings
                            if (el && observerRef.current) {
                              observerRef.current.observe(el);
                            }
                          }}
                          className="rounded-xl border p-3"
                          style={{
                            borderColor: BLUE_ACCENT,
                            background: 'rgba(59, 123, 244, 0.04)',
                          }}
                        >
                          {/* Header with first verse chapter info */}
                          <div
                            className="flex items-center gap-2 flex-wrap mb-3"
                            dir={i18n.language?.startsWith('he') ? 'rtl' : 'ltr'}
                          >
                            <span
                              data-aid={`verse-group-label-${startIndex}`}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[11px] font-medium"
                              style={{ background: BLUE_ACCENT }}
                            >
                              {t('verse_label_full', {
                                chapter:
                                  i18n.language?.startsWith('he') && fontMode !== 'translit'
                                    ? numberToHebrewLetters(groupItems[0]?.chapter ?? 0)
                                    : String(groupItems[0]?.chapter ?? ''),
                                pasuk:
                                  i18n.language?.startsWith('he') && fontMode !== 'translit'
                                    ? numberToHebrewLetters(groupItems[0]?.pasuk ?? 0)
                                    : String(groupItems[0]?.pasuk ?? ''),
                              })}
                            </span>
                          </div>
                          {/* Combined Hebrew text with karaoke support */}
                          <div
                            data-aid={`verse-group-hebrew-text-${startIndex}`}
                            dir={textMode === 'hebrew' && fontMode !== 'translit' ? 'rtl' : 'ltr'}
                            className={fontMode === 'torah' ? 'font-torah' : ''}
                            style={{
                              color: TEXT_DARK,
                              fontSize: '1.125rem',
                              lineHeight: '2',
                              letterSpacing: 0,
                              ...(fontMode !== 'torah'
                                ? {
                                    fontFamily: '"Noto Serif Hebrew", "David Libre", serif',
                                    fontSynthesis: 'none',
                                    WebkitFontSmoothing: 'antialiased',
                                  }
                                : {}),
                            }}
                            onClick={(e) => {
                              if (selectedInGroup) {
                                e.stopPropagation();
                                cycleFontMode();
                              }
                            }}
                          >
                            {textMode === 'hebrew'
                              ? groupItems.map((v, localIdx) => {
                                  const idx = startIndex + localIdx;
                                  // Check if karaoke should be shown for this verse
                                  let isInRecordingGroup = false;
                                  let versePositionInRecordingGroup = -1;
                                  if (isRecording && recordingIndex !== null) {
                                    let targetIndices: number[] = [recordingIndex];
                                    if (grouped) {
                                      const grp = groupedVerses.find(
                                        (g) =>
                                          recordingIndex >= g.startIndex &&
                                          recordingIndex < g.startIndex + g.verses.length,
                                      );
                                      if (grp)
                                        targetIndices = grp.verses.map(
                                          (_, i) => grp.startIndex + i,
                                        );
                                    }
                                    versePositionInRecordingGroup = targetIndices.indexOf(idx);
                                    isInRecordingGroup = versePositionInRecordingGroup >= 0;
                                  }

                                  let isInPlayingGroup = false;
                                  if (isPlaying && playingIndex !== null && grouped) {
                                    const playingGroup = groupedVerses.find(
                                      (g) =>
                                        playingIndex >= g.startIndex &&
                                        playingIndex < g.startIndex + g.verses.length,
                                    );
                                    if (playingGroup) {
                                      isInPlayingGroup =
                                        playingGroup.verses.findIndex(
                                          (_, i) => playingGroup.startIndex + i === idx,
                                        ) >= 0;
                                    }
                                  }

                                  const shouldShowKaraoke =
                                    (playingIndex !== null &&
                                      idx === playingIndex &&
                                      alignmentsByVerse[idx]) ||
                                    (isPlaying && isInPlayingGroup && alignmentsByVerse[idx]) ||
                                    (isRecording && isInRecordingGroup && alignmentsByVerse[idx]);

                                  // Use words from torah.json (v.hebrew) for accurate display
                                  const verseWords = v.hebrew
                                    .replace(/־/g, ' ')
                                    .split(/\s+/)
                                    .filter(Boolean);
                                  const translitWords = (v.transliteration ?? '')
                                    .split(/\s+/)
                                    .filter(Boolean);

                                  // Some verses (e.g. haftara) have no transliteration data; fall
                                  // back to Hebrew word rendering rather than showing a blank verse.
                                  const hasTranslitWords = translitWords.length > 0;

                                  return (
                                    <span key={idx}>
                                      {shouldShowKaraoke &&
                                      fontMode === 'translit' &&
                                      hasTranslitWords
                                        ? translitWords.map((word: string, wIdx: number) => {
                                            const isHighlighted =
                                              (playingIndex !== null &&
                                                idx === playingIndex &&
                                                currentWordIndex === wIdx) ||
                                              (isRecording &&
                                                isInRecordingGroup &&
                                                currentVerseInGroup ===
                                                  versePositionInRecordingGroup &&
                                                currentWordIndex === wIdx);
                                            return (
                                              <span
                                                key={wIdx}
                                                style={{
                                                  color: isHighlighted ? 'red' : undefined,
                                                  transition: 'color 0.15s linear',
                                                }}
                                              >
                                                {word}{' '}
                                              </span>
                                            );
                                          })
                                        : shouldShowKaraoke
                                          ? verseWords.map((word: string, wIdx: number) => {
                                              const isHighlighted =
                                                (playingIndex !== null &&
                                                  idx === playingIndex &&
                                                  currentWordIndex === wIdx) ||
                                                (isRecording &&
                                                  isInRecordingGroup &&
                                                  currentVerseInGroup ===
                                                    versePositionInRecordingGroup &&
                                                  currentWordIndex === wIdx);
                                              return (
                                                <span
                                                  key={wIdx}
                                                  style={{
                                                    color: isHighlighted ? 'red' : undefined,
                                                    transition: 'color 0.15s linear',
                                                  }}
                                                >
                                                  {fontMode !== 'torah'
                                                    ? renderHebrewWithShvaNa(word)
                                                    : formatHebrewText(word)}{' '}
                                                </span>
                                              );
                                            })
                                          : fontMode === 'translit'
                                            ? (v.transliteration ??
                                              renderHebrewWithShvaNa(v.hebrew))
                                            : fontMode !== 'torah'
                                              ? renderHebrewWithShvaNa(v.hebrew)
                                              : formatHebrewText(v.hebrew)}
                                      {localIdx < groupItems.length - 1 && ' '}
                                    </span>
                                  );
                                })
                              : groupItems.map((v, localIdx) => (
                                  <span key={startIndex + localIdx}>
                                    {v.transliteration ?? v.hebrew}
                                    {localIdx < groupItems.length - 1 && ' '}
                                  </span>
                                ))}
                          </div>
                          {/* Combined transliteration box */}
                          {textMode === 'hebrew' && showTransliteration ? (
                            <div
                              data-aid={`verse-group-transliteration-box-${startIndex}`}
                              className="bg-gray-50 rounded-xl p-3 text-sm mt-3"
                            >
                              <div
                                className="text-[10px] font-semibold mb-1.5"
                                style={{ color: TEXT_SECONDARY }}
                              >
                                {t('transliteration_text') || 'Transliteration'}
                              </div>
                              <div className="leading-relaxed" style={{ color: TEXT_DARK }}>
                                {groupItems.map((v, localIdx) => (
                                  <span key={startIndex + localIdx}>
                                    {v.transliteration}
                                    {localIdx < groupItems.length - 1 && ' '}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : textMode === 'translit' ? (
                            <div
                              data-aid={`verse-group-hebrew-box-${startIndex}`}
                              className="bg-gray-50 rounded-xl p-3 text-sm mt-3"
                              dir="rtl"
                            >
                              <div
                                className="text-[10px] font-semibold mb-1.5"
                                style={{ color: TEXT_SECONDARY }}
                              >
                                {t('hebrew_text') || 'Hebrew'}
                              </div>
                              <div className="leading-relaxed" style={{ color: TEXT_DARK }}>
                                {groupItems.map((v, localIdx) => (
                                  <span key={startIndex + localIdx}>
                                    {formatHebrewText(v.hebrew)}
                                    {localIdx < groupItems.length - 1 && ' '}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}
                      {/* Action buttons - always shown for each group */}
                      {(() => {
                        // For grouped mode, use startIndex; for single mode, use verseIndex
                        const idx = versesPerGroup === 1 ? verseIndex : startIndex;
                        // Only show buttons for single mode if this group contains the selected verse
                        if (versesPerGroup === 1 && !selectedInGroup) return null;
                        const groupIndices = groupItems.map((_, i) => startIndex + i);
                        const v = verses[idx];
                        const isVersePlaying =
                          isPlaying && playingIndex === idx && groupPlaybackQueue.length === 0; // single verse playing state
                        // Group is playing if currently playing any verse in the group OR queue contains group verses
                        const isGroupPlaying =
                          isPlaying &&
                          (groupIndices.includes(playingIndex!) ||
                            groupPlaybackQueue.some((qIdx) => groupIndices.includes(qIdx)));
                        const isVerseRecording =
                          isRecording && groupIndices.includes(recordingIndex!);
                        const isVerseComparing =
                          isComparing && groupIndices.includes(comparingIndex!);
                        // Check for local recording OR S3 recording
                        const hasUserRecording =
                          !!recordBlobs[startIndex] || !!userAudioUrls[startIndex];
                        const isUserVersePlaying =
                          isUserPlaying && groupIndices.includes(userPlayingIndex!);
                        const actionsLocked =
                          PAYMENT_RESTRICTION_ENABLED &&
                          showPayments &&
                          !hasAccess &&
                          startIndex >= 3;
                        const showRestrictionMessage = actionsLocked;
                        return (
                          <div
                            data-aid={`verse-actions-${startIndex}`}
                            className="mt-4 pt-3 border-t flex flex-col gap-2"
                          >
                            {showRestrictionMessage && (
                              <div
                                data-aid={`payment-restriction-message-${startIndex}`}
                                className="rounded-xl px-3 py-2 text-[12px] flex items-center justify-between gap-2 flex-wrap"
                                style={{
                                  background: '#FFFBEB',
                                  border: '1px solid #FCD34D',
                                  color: '#92400E',
                                }}
                              >
                                <span>{t('practice_subscribe_to_unlock')}</span>
                                <button
                                  data-aid={`subscribe-button-${startIndex}`}
                                  type="button"
                                  onClick={() => navigate('/payments')}
                                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold"
                                  style={{
                                    background: GOLD_BUTTON,
                                    color: TEXT_DARK,
                                  }}
                                >
                                  {t('profile_psukim_go_to_subscriptions')}
                                </button>
                              </div>
                            )}
                            <div
                              data-aid={`verse-action-buttons-${startIndex}`}
                              className="flex items-center gap-2 flex-wrap"
                            >
                              {/* Play/Pause */}
                              <button
                                data-aid={`play-pause-button-${startIndex}`}
                                disabled={actionsLocked}
                                onClick={() =>
                                  actionsLocked
                                    ? undefined
                                    : versesPerGroup !== 1
                                      ? handlePlayGroup(groupIndices)
                                      : handlePlay(idx, v)
                                }
                                className="px-4 h-10 rounded-full text-xs font-semibold flex items-center gap-1 border transition-colors"
                                style={{
                                  background: actionsLocked
                                    ? '#d1d5db'
                                    : isGroupPlaying || isVersePlaying
                                      ? BLUE_ACCENT
                                      : 'rgba(59, 123, 244, 0.08)',
                                  borderColor: actionsLocked
                                    ? '#d1d5db'
                                    : isGroupPlaying || isVersePlaying
                                      ? BLUE_ACCENT
                                      : BLUE_ACCENT,
                                  color: actionsLocked
                                    ? '#6b7280'
                                    : isGroupPlaying || isVersePlaying
                                      ? 'white'
                                      : BLUE_ACCENT,
                                  cursor: actionsLocked ? 'not-allowed' : undefined,
                                }}
                                aria-label={
                                  versesPerGroup !== 1
                                    ? isGroupPlaying
                                      ? 'Pause group playback'
                                      : 'Play group'
                                    : isVersePlaying
                                      ? 'Pause rabbi audio'
                                      : 'Play rabbi audio'
                                }
                              >
                                {isGroupPlaying || isVersePlaying
                                  ? '❚❚ ' + (t('pause') || 'Pause')
                                  : '▶ ' + (t('play') || 'Play')}
                              </button>
                              {/* Restart button - shown when playbackTime > 0 */}
                              {playbackTime > 0 && (
                                <button
                                  data-aid={`restart-button-${startIndex}`}
                                  onClick={() =>
                                    actionsLocked
                                      ? undefined
                                      : versesPerGroup !== 1
                                        ? restartGroupPlayback(groupIndices)
                                        : restartSinglePlayback(idx)
                                  }
                                  disabled={actionsLocked || isVerseRecording}
                                  className="px-3 h-10 rounded-full text-xs font-semibold flex items-center gap-1 border transition-colors"
                                  style={{
                                    background: isVerseRecording
                                      ? '#d1d5db'
                                      : 'rgba(59, 123, 244, 0.08)',
                                    borderColor: isVerseRecording ? '#d1d5db' : BLUE_ACCENT,
                                    color: isVerseRecording ? '#6b7280' : BLUE_ACCENT,
                                    cursor: isVerseRecording ? 'not-allowed' : 'pointer',
                                  }}
                                  aria-label={t('practice_restart') || 'Restart'}
                                >
                                  ⟲ {t('practice_restart') || 'Restart'}
                                </button>
                              )}
                              {/* Record button - always enabled before and after recording (when subscribed) */}
                              <button
                                data-aid={`record-button-${startIndex}`}
                                type="button"
                                disabled={actionsLocked}
                                onClick={() =>
                                  actionsLocked
                                    ? undefined
                                    : isVerseRecording
                                      ? stopRecording()
                                      : startRecording(startIndex)
                                }
                                className="px-3 h-10 rounded-full text-xs font-semibold flex items-center gap-1 border transition-colors"
                                style={{
                                  background: actionsLocked
                                    ? '#d1d5db'
                                    : isVerseRecording
                                      ? TEXT_DARK
                                      : GOLD_BUTTON,
                                  borderColor: actionsLocked
                                    ? '#d1d5db'
                                    : isVerseRecording
                                      ? TEXT_DARK
                                      : GOLD_BUTTON,
                                  color: actionsLocked
                                    ? '#6b7280'
                                    : isVerseRecording
                                      ? 'white'
                                      : TEXT_DARK,
                                  cursor: actionsLocked ? 'not-allowed' : 'pointer',
                                }}
                                aria-label={
                                  isVerseRecording ? t('stop') || 'Stop' : t('record') || 'Record'
                                }
                              >
                                {isVerseRecording
                                  ? '■ ' + t('stop')
                                  : '🎙️ ' + (t('record') || 'Record')}
                              </button>
                              {/* Compare button - run comparison (can re-compare after recording again) */}
                              <button
                                data-aid={`compare-button-${startIndex}`}
                                type="button"
                                onClick={() => {
                                  if (actionsLocked) return;
                                  if (hasAIConsent()) {
                                    handleCompare(startIndex);
                                  } else {
                                    setPendingCompareIndex(startIndex);
                                    setShowAIConsentDialog(true);
                                  }
                                }}
                                disabled={
                                  actionsLocked ||
                                  !hasUserRecording ||
                                  isVerseRecording ||
                                  isVerseComparing
                                }
                                className="px-3 h-10 rounded-full text-xs font-semibold flex items-center gap-1 border transition-colors"
                                style={{
                                  background:
                                    actionsLocked || !hasUserRecording || isVerseRecording
                                      ? '#d1d5db'
                                      : isVerseComparing
                                        ? TEXT_DARK
                                        : BLUE_ACCENT,
                                  borderColor:
                                    actionsLocked || !hasUserRecording || isVerseRecording
                                      ? '#d1d5db'
                                      : isVerseComparing
                                        ? TEXT_DARK
                                        : BLUE_ACCENT,
                                  color:
                                    actionsLocked || !hasUserRecording || isVerseRecording
                                      ? '#6b7280'
                                      : 'white',
                                  cursor:
                                    actionsLocked ||
                                    !hasUserRecording ||
                                    isVerseRecording ||
                                    isVerseComparing
                                      ? 'not-allowed'
                                      : 'pointer',
                                  animation: isVerseComparing
                                    ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                                    : newResultsSet.has(startIndex)
                                      ? 'slowBlink 1s ease-in-out 2'
                                      : undefined,
                                }}
                                aria-label={t('compare') || 'Compare'}
                              >
                                {isVerseComparing
                                  ? t('analyze') + '…'
                                  : '📊 ' + (t('compare') || 'Compare')}
                              </button>
                              {/* View score button - separate so user can record & compare again */}
                              {comparisonResultsByGroup[startIndex]?.score !== undefined && (
                                <button
                                  ref={getScoreBadgeRef(startIndex)}
                                  type="button"
                                  onClick={() => {
                                    setNewResultsSet((prev) => {
                                      const next = new Set(prev);
                                      next.delete(startIndex);
                                      return next;
                                    });
                                    handleViewResults(startIndex);
                                  }}
                                  disabled={isVerseComparing}
                                  className="px-3 h-10 rounded-full text-xs font-semibold flex items-center gap-1 border transition-colors"
                                  style={{
                                    background:
                                      comparisonResultsByGroup[startIndex]?.score >= 70
                                        ? '#0d8f4d'
                                        : comparisonResultsByGroup[startIndex]?.score >= 40
                                          ? '#f59e0b'
                                          : '#e67e22',
                                    borderColor:
                                      comparisonResultsByGroup[startIndex]?.score >= 70
                                        ? '#0d8f4d'
                                        : comparisonResultsByGroup[startIndex]?.score >= 40
                                          ? '#f59e0b'
                                          : '#e67e22',
                                    color: 'white',
                                    cursor: isVerseComparing ? 'not-allowed' : 'pointer',
                                    animation: newResultsSet.has(startIndex)
                                      ? 'slowBlink 1.5s ease-in-out infinite'
                                      : undefined,
                                  }}
                                  aria-label={t('view_results') || 'View Results'}
                                >
                                  🏆 {comparisonResultsByGroup[startIndex].score}%
                                </button>
                              )}
                              {/* Play user recording button */}
                              {hasUserRecording && (
                                <button
                                  onClick={() =>
                                    actionsLocked ? undefined : handlePlayUser(startIndex)
                                  }
                                  disabled={actionsLocked || !hasUserRecording || isVerseRecording}
                                  className={
                                    'w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold transition-colors ' +
                                    (hasUserRecording && isUserVersePlaying
                                      ? 'bg-[#0d8f4d] text-white border border-[#0d8f4d]'
                                      : 'bg-[#f0faf5] border border-[#0d8f4d] text-[#0d8f4d] disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500')
                                  }
                                  aria-label={
                                    isUserVersePlaying ? 'Pause my recording' : 'Play my recording'
                                  }
                                >
                                  {isUserVersePlaying ? '❚❚' : '▶'}
                                </button>
                              )}
                              {/* Share button */}
                              {hasUserRecording && (
                                <button
                                  onClick={() =>
                                    actionsLocked ? undefined : handleShare(startIndex)
                                  }
                                  disabled={actionsLocked || isVerseRecording}
                                  className={
                                    'w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold transition-colors ' +
                                    (!isVerseRecording
                                      ? 'bg-[#f0f9ff] border border-[#0d8f4d] text-[#0d8f4d] hover:bg-[#e3f5ff]'
                                      : 'bg-gray-300 border-gray-300 text-gray-500')
                                  }
                                  aria-label={t('share') || 'Share recording'}
                                >
                                  <img
                                    src={Share}
                                    alt={t('share') || 'Share'}
                                    style={{
                                      maxWidth: 'none',
                                      height: '17px',
                                      width: '17px',
                                    }}
                                  />
                                </button>
                              )}
                              {/* Request Rabbi Review button */}
                              {/*{hasUserRecording && (*/}
                              {/*  <button*/}
                              {/*    onClick={() =>*/}
                              {/*      actionsLocked ? undefined : handleRequestRabbiReview(startIndex)*/}
                              {/*    }*/}
                              {/*    disabled={*/}
                              {/*      actionsLocked ||*/}
                              {/*      isVerseRecording ||*/}
                              {/*      sendingReviewRequest === startIndex ||*/}
                              {/*      reviewRequestSent.has(startIndex)*/}
                              {/*    }*/}
                              {/*    className={*/}
                              {/*      'px-3 h-10 rounded-full text-xs font-semibold flex items-center gap-1 border transition-colors ' +*/}
                              {/*      (reviewRequestSent.has(startIndex)*/}
                              {/*        ? 'bg-[#22c55e] border-[#22c55e] text-white'*/}
                              {/*        : sendingReviewRequest === startIndex*/}
                              {/*          ? 'bg-[#6366f1] border-[#6366f1] text-white animate-pulse'*/}
                              {/*          : !isVerseRecording*/}
                              {/*            ? 'bg-[#6366f1] border-[#6366f1] text-white hover:bg-[#4f46e5]'*/}
                              {/*            : 'bg-gray-300 border-gray-300 text-gray-500')*/}
                              {/*    }*/}
                              {/*    aria-label={*/}
                              {/*      reviewRequestSent.has(startIndex)*/}
                              {/*        ? t('review_sent') || 'Sent'*/}
                              {/*        : t('request_rabbi_review') || 'Ask Rabbi'*/}
                              {/*    }*/}
                              {/*  >*/}
                              {/*    {reviewRequestSent.has(startIndex) ? (*/}
                              {/*      <>✓ {t('sent') || 'Sent'}</>*/}
                              {/*    ) : sendingReviewRequest === startIndex ? (*/}
                              {/*      <>{t('sending') || 'Sending'}...</>*/}
                              {/*    ) : (*/}
                              {/*      <>📨 {t('ask_rabbi') || 'Ask Rabbi'}</>*/}
                              {/*    )}*/}
                              {/*  </button>*/}
                              {/*)}*/}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </main>
      <audio
        ref={audioRef}
        onEnded={() => {
          // If we have a playback queue (group) continue
          if (groupPlaybackQueue.length > 0) {
            const nextIdx = groupPlaybackQueue[0];
            const rest = groupPlaybackQueue.slice(1);
            setGroupPlaybackQueue(rest);
            if (verses[nextIdx]) {
              handlePlay(nextIdx, verses[nextIdx]);
              return; // keep playing
            }
          }
          setIsPlaying(false);
          setPlayingIndex(null);
          setGroupPlaybackQueue([]);
        }}
      />
      <audio
        ref={userAudioRef}
        onEnded={() => {
          setIsUserPlaying(false);
          setUserPlayingIndex(null);
        }}
      />
      {/* Missing Verse Popup */}
      {showRabbiAudioNotFound && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRabbiAudioNotFound(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-center mb-4">
              <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-gradient-to-br from-[#3B7BF4] to-[#6D5AE6]">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                  <circle cx="10.5" cy="10.5" r="6.5" stroke="#fff" strokeWidth="2" />
                  <line
                    x1="15.2"
                    y1="15.2"
                    x2="20.5"
                    y2="20.5"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <text
                    x="10.5"
                    y="14"
                    textAnchor="middle"
                    fontSize="8.5"
                    fontWeight="700"
                    fill="#fff"
                    fontFamily="Arial, sans-serif"
                  >
                    ?
                  </text>
                </svg>
              </span>
            </div>
            <h3 className="text-lg font-bold text-center mb-2" style={{ color: TEXT_DARK }}>
              {t('missing_verse_popup_title')}
            </h3>
            <p className="text-center mb-4" style={{ color: TEXT_SECONDARY }}>
              {t('missing_verse_popup_message')}
            </p>
            {missingVerseNotifySent ? (
              <div className="text-center font-bold text-sm mb-2.5" style={{ color: BLUE_ACCENT }}>
                {t('missing_verse_notify_confirmation')}
              </div>
            ) : (
              <button
                onClick={handleNotifyWhenVerseReady}
                className="w-full py-3 rounded-xl text-white font-semibold transition-colors mb-2.5"
                style={{ background: BLUE_ACCENT }}
              >
                {t('missing_verse_notify_button')}
              </button>
            )}
            <button
              onClick={() => setShowRabbiAudioNotFound(false)}
              className="w-full py-3 rounded-xl font-semibold border transition-colors"
              style={{ color: TEXT_SECONDARY, borderColor: '#D1D5DB' }}
            >
              {t('ok')}
            </button>
          </div>
        </div>
      )}
      <BottomNav />
    </div>
  );
};

export default PracticePage;
